/**
 * Thin fetch wrapper for an OPBridge instance.
 *
 * Each instance is tied to one (baseUrl, bearer) pair. Per-request
 * timeout via AbortController — OPBridge is "up and down like a yoyo"
 * during active development, so failed connections must abort quickly
 * (not hang on the default TCP RST/SYN retry timeout) so the next poll
 * can retry against a freshly-restarted bridge.
 *
 * No retry logic at this layer — that's the connection manager's job.
 * No error spam — callers decide what to do with rejected promises.
 */

import { WtTransportError, type WtConnection } from './wtTransport.ts';

export interface OpbridgeHealth {
  ok: boolean;
  app: string;
  protocol_version: string;
  build_version: string;
  platform: 'windows' | 'macos' | string;
  port: number;
  capabilities: Record<string, unknown>;
  tier_metrics?: Record<string, unknown>;
  /** Per-machine DISPLAY label. v1.13.9+ populated it from the OS hostname
   *  (Windows `COMPUTERNAME`; Mac `gethostname(3)` minus a trailing
   *  `.local`). v1.14.9+ evolved the semantics: it now returns the
   *  operator's friendly name when one is set (renamed in the dashboard
   *  Mesh panel), else the raw hostname — i.e. `friendly ?? hostname`. Treat
   *  it as a display string ONLY, never as identity (key identity on
   *  `node_id`). Absent on pre-v1.13.9 daemons — callers fall back to
   *  parsing the middle segment of `instance_name`. */
  machine_name?: string | null;
  /** Raw OS hostname, added additively by OPBridge v1.14.9+. ALWAYS the
   *  true hostname even when `machine_name` carries an operator override.
   *  Use this (not `machine_name`) if you ever need the literal hostname. */
  hostname?: string | null;
  /** This bridge's LAN IPv4 as the mesh sees it (multi-homed-aware), added
   *  additively by OPBridge v1.14.9+. Optional display only. */
  ip?: string | null;
  /** Stable per-instance identifier of the form
   *  `<scope>-<machine_name>-<6char_id>` (e.g. `local-GAMING-PC-OZll6G`).
   *  The middle segment is the same value as the hostname-derived
   *  `machine_name` on v1.13.9+; used as the fallback machine-name parse
   *  for older daemons. */
  instance_name?: string | null;
  /** Operator-set human name for who runs this bridge, e.g. "ortopilot's
   *  dev PC". Opt-in and NEVER defaulted to the OS username — publishing a
   *  real person's name on an unauthenticated LAN endpoint by default would
   *  be a real privacy leak, so absence is the normal case, not an error.
   *  Set via the OPBridge dashboard/admin (OPBridge S175, 2026-08-02). */
  owner?: string | null;
  /** DEC-526 (OPBridge). The real, publicly-trusted per-device hostname
   *  (`<hex>.bridge.overpoweredapps.net`) once a suite cert has been
   *  issued for this daemon; `null`/absent otherwise (fresh install,
   *  offline daemon, or issuance not yet run). When present, prefer it
   *  over the LAN IP / `opbridge.local` self-signed path — zero cert
   *  warnings, real CA. Requires internet (real DNS + a real CA chain),
   *  so the self-signed/raw-IP paths must stay the permanent fallback for
   *  offline-gig connectivity, never replaced. Added OPBridge v1.15.76. */
  suite_hostname?: string | null;
  /** The port the browser-HTTPS listener actually binds on for this
   *  daemon (`Config::browser_tls_port` override if set, else the base
   *  port + 3). Pair with `suite_hostname` to build the real URL —
   *  don't hardcode `+3` client-side, it silently breaks under an
   *  operator's port override. Added OPBridge v1.15.76. */
  browser_tls_port?: number | null;
  /** Unix seconds at which the SUITE cert expires, so the PWA can warn
   *  before a cert strands the rig. Added OPBridge v1.15.9x. */
  suite_cert_expires_at_unix_s?: number | null;
  /** The OFFLINE path (OPBridge v1.15.97+, 2026-08-31). Present only when the
   *  daemon's WebTransport front door actually bound - a listener that failed
   *  to start advertises nothing, deliberately, so a consumer can never pin
   *  to an endpoint that does not exist.
   *
   *  `cert_sha256` is the pin for a SELF-SIGNED cert: it needs no CA and no
   *  DNS, which is the entire reason this path survives a venue with no
   *  internet. It rotates (validity is <= 14 days by spec), so it must be
   *  re-read from every successful `/health` and never treated as constant. */
  webtransport?: {
    port: number;
    cert_sha256: string;
    expires_at_unix_s: number;
    /** OPTIONAL, and the client half is built ahead of the daemon (2026-09-01
     *  ask). Hashes of certs the bridge has PRE-MINTED and will present in
     *  future rotations. The 14-day cap is per certificate, so publishing a
     *  chain is spec-legal and turns a ~10-day offline window into months.
     *
     *  Absent on every daemon today, which is why nothing here may require
     *  it: with it missing we hold the single current pin and behave exactly
     *  as before. Order is not significant - the browser matches whichever
     *  cert is live. */
    cert_sha256_upcoming?: string[] | null;
    /** When the LAST pre-minted cert in `cert_sha256_upcoming` expires, i.e.
     *  how long the cached chain stays usable with no contact at all. Used
     *  instead of `expires_at_unix_s` to decide whether the pin set is dead. */
    upcoming_valid_until_unix_s?: number | null;
  } | null;
}

/** Resolve the machine label for a bridge from its /health response.
 *  Prefers v1.13.9+'s `machine_name`; falls back to parsing
 *  `instance_name`'s middle segment (`local-<MACHINE>-<id>`). Returns
 *  null if neither field is present. The parse strips the leading
 *  `<scope>-` and trailing `-<6char_id>`; machine names CAN contain
 *  hyphens (`GAMING-PC`), so the trailing strip is "drop the last
 *  hyphen-segment", not "split('-')[1]". */
export function resolveMachineName(h: OpbridgeHealth | null): string | null {
  if (!h) return null;
  if (h.machine_name) return h.machine_name;
  return machineNameFromInstanceName(h.instance_name);
}

/** The same parse, over a STORED `bridge_instance_name` rather than a live
 *  /health body. Split out so a surface that names MACHINES (the OBS
 *  Computer picker) does not have to fake a health response or grow a
 *  second copy of the hyphen rule. */
export function machineNameFromInstanceName(ins: string | undefined | null): string | null {
  if (!ins) return null;
  const parts = ins.split('-');
  if (parts.length < 3) return null;
  // Drop first segment (scope) and last segment (random id); rejoin
  // the rest so multi-hyphen machine names ("GAMING-PC") survive.
  return parts.slice(1, -1).join('-') || null;
}

/** OPBridge's `FixtureInstance` Serde struct, ALL ELEVEN FIELDS.
 *
 *  `[WIDENED 2026-09-17]` - this used to be "minimum fields the PWA cares
 *  about" and modelled eight. That is safe for reading and unsafe the moment
 *  anything writes: `PUT /fixtures/{id}` takes a whole instance and
 *  OPBridge's update path never re-stamps `output_node_id` (the body owns
 *  it), so an eight-field body silently strips the mesh owner (DEC-471), the
 *  environment stamp (DEC-470) and the operator's groups. `createFixture` /
 *  `updateFixture` below have existed uncalled since they were written; this
 *  makes them safe to call before anything does. */
export interface FixtureInstance {
  id: string;
  profileId: string;
  mode?: string | null;
  /** Operator-authored display name. Absent on rigs where it was never set
   *  (pre-2026-08-02 fixtures, or any fixture never renamed) — consumers
   *  fall back to `notes`, then a synthesised label. */
  name?: string | null;
  universe: number;
  /** `0` means "no DMX address" - OPBridge's deliberate sentinel for a
   *  non-DMX transport, not a missing value. */
  dmxAddress: number;
  positionHint?: { x: number; y: number; z?: number } | null;
  notes?: string | null;
  /** Mesh node that drives this fixture (DEC-471). Never authored by us. */
  outputNodeId?: string | null;
  /** Operator group tags. `Vec<String>` on the wire, so absent means none. */
  groups?: string[];
  /** Venue-context stamp (DEC-470). Never authored by us. */
  environmentId?: string | null;
  /** Non-DMX transport descriptor. Opaque to the PWA. */
  transport?: unknown;
  /** Additive fields from a newer daemon. Tolerated and carried, per the
   *  same unknown-field discipline the timeline envelope uses. */
  [extra: string]: unknown;
}

export interface FixturesListResponse {
  fixtures: FixtureInstance[];
}

// ─── DMX profile catalogue (GET /dmx/profiles) ────────────────────────────
// Build-time const data on OPBridge, not user-scoped: twelve vendored
// profiles across the seven-name archetype vocabulary. Reachable on the data
// plane with the `opbuttons-full` bearer (`/dmx/**`), verified 200 on
// 2026-09-17. The PWA modelled none of it until then and resolved profiles
// from a hardcoded one-entry table instead.

export interface DmxProfileSubfixture {
  index: number;
  name: string;
  capabilities: string[];
}

export interface DmxProfileMode {
  name: string;
  channelCount: number;
  /** Capability NAMES, not channel records - the catalogue sends names and
   *  the byte offsets stay OPBridge's business (DEC-014's moat: we never
   *  learn channel numbers). */
  capabilities: string[];
  /** Built-in FX macro slot names declared by this mode. */
  effects: string[];
  /** Addressable zones. Empty for single-block fixtures. */
  subfixtures: DmxProfileSubfixture[];
}

export interface DmxProfile {
  id: string;
  name: string;
  manufacturer: string;
  model: string;
  /** One of the seven archetypes, or `null` for a profile OPBridge declines
   *  to bucket. Never inferred from `id`. */
  archetype?: string | null;
  modes: DmxProfileMode[];
}

export interface DmxProfilesResponse {
  profiles: DmxProfile[];
}

/** One of OPBridge's own software effects. `free` are wall-clock and
 *  phase-locked across the rig; `musical` are driven by OPBridge's musical
 *  clock and idle when the transport is stopped. Kept as two groups because
 *  that difference is the whole reason to pick one over the other. */
export interface DmxSoftwareEffect {
  name: string;
  label: string;
}

export interface DmxFxCatalogResponse {
  free: DmxSoftwareEffect[];
  musical: DmxSoftwareEffect[];
}

// ─── Resolved fixture state (GET /dmx/fixtures/resolved) ──────────────────
// What is ACTUALLY on the wire, reverse-resolved from the live DMX buffer,
// plus OPBridge's hold/park ownership model. The one place in this whole app
// that gets genuine closed-loop feedback: MIDI tells us a send succeeded, this
// tells us what the light is doing.

/** One capability currently held by a writer, and by whom. `source` says what
 *  KIND of writer (`fx`, a modulator, a direct set); `stale` marks a hold
 *  whose owner's link has died - a hold nobody will ever release, which is
 *  what `POST /dmx/fixtures/release-abandoned` exists to clear. */
export interface DmxHeldCapability {
  capability: string;
  by: string;
  source?: string;
  stale?: boolean;
  subfixture?: number | null;
  since?: number;
}

export interface DmxResolvedFixture {
  id: string;
  /** Null when this OPBridge cannot say: a fixture another computer owns (`live: false`, `owner` its node), read
   *  before that computer's state reached this one. Measured on the dev PC 2026-11-05 with STREAMPC owning the
   *  lights: one read in a few came back `{rgb: null, intensity: null}`, and the readout threw reading `.r`. */
  rgb: { r: number; g: number; b: number } | null;
  /** 0-255 master level; null as `rgb` is. */
  intensity: number | null;
  /** Power. `false` means blacked but the colour is remembered. */
  on: boolean;
  /** Something is driving this fixture right now. */
  held?: boolean;
  /** Who: `"playback"` for a running timeline/effect, a consumer name, etc.
   *  The reason a tap can appear to do nothing and the operator deserves to
   *  be told which. */
  heldBy?: string | null;
  heldCapabilities?: DmxHeldCapability[];
  heldSince?: number;
  heldStale?: boolean;
  /** Parked (DEC-482): held at a value and excluded from playback until an
   *  explicit release. Deliberate, unlike a stale hold. */
  parked?: boolean;
  parkedBy?: string | null;
  [extra: string]: unknown;
}

export interface DmxResolvedResponse {
  fixtures: DmxResolvedFixture[];
}

// OPBridge `EnginesStateSnapshot` envelope. camelCase on the wire
// (`#[serde(rename_all = "camelCase")]`). Lifecycle is lowercase.
export type EngineLifecycle = 'idle' | 'armed' | 'running' | 'killing' | 'stopping';

export interface OpbridgeLfoTargetMidi {
  type: 'midi';
  port: string;
  channel: number;
  ccNumber: number;
}
export interface OpbridgeLfoTargetOsc {
  type: 'osc';
  host: string;
  port: number;
  address: string;
  argIndex?: number;
}
export interface OpbridgeLfoTargetDmx {
  type: 'dmx';
  fixtureId: string;
  subfixtureIndex?: number;
  capability: string;
}
export type OpbridgeLfoTarget =
  | OpbridgeLfoTargetMidi
  | OpbridgeLfoTargetOsc
  | OpbridgeLfoTargetDmx;

export interface OpbridgeLfoParams {
  id: string;
  target: OpbridgeLfoTarget;
  periodBeats: number;
  amplitude: number;
  center: number;
  waveform: 'sine' | 'saw';
  loop: boolean;
}

export interface OpbridgeLfoStateSnapshot {
  id: string;
  state: EngineLifecycle;
  startBeat: number;
  currentPhase: number;
  /** Engine-space value in `[0, 1]`. PWA renormalises to MIDI 0..127. */
  currentValue: number;
  params: OpbridgeLfoParams;
}

/** One sequencer runtime in the engines-state envelope. Mirrors OPBridge's
 *  `SequencerStateSnapshot` (camelCase on the wire). The PWA maps `state` +
 *  `lastFiredSteps` onto its own `SequencerSnapshot`; `def` is the engine's
 *  arm-time copy of the pattern — the PWA already owns the canonical library
 *  so it's carried as opaque here. */
export interface OpbridgeSequencerStateSnapshot {
  id: string;
  state: EngineLifecycle;
  startBeat: number;
  lastFiredSteps: number[];
  def?: unknown;
}

/** One macro runtime in the engines-state envelope. Mirrors OPBridge's
 *  `MacroStateSnapshot` (camelCase). `laneProgress` (0..1 per lane) +
 *  `laneValues` (0..127 per lane) map 1:1 onto the PWA's `MacroStateMessage`. */
export interface OpbridgeMacroStateSnapshot {
  id: string;
  state: EngineLifecycle;
  startBeat: number;
  laneProgress: number[];
  laneValues: number[];
  def?: unknown;
}

/** One toggle in `GET /toggles/{consumer}`'s library — byte-verified live
 *  2026-08-16 (camelCase `onAction`/`offAction`, `state` is the current
 *  bool). `onAction`/`offAction` reuse {@link RuleAction} — OPBridge's own
 *  contract: "the exact same shape your midi/osc rule actions already use." */
export interface ToggleState {
  id: string;
  name?: string;
  onAction: RuleAction;
  offAction: RuleAction;
  state: boolean;
}

export interface TogglesLibrary {
  schemaVersion: string;
  toggles: ToggleState[];
}

export interface EnginesStateSnapshotResponse {
  consumer: string;
  userId: string;
  lfos: OpbridgeLfoStateSnapshot[];
  sequencers?: OpbridgeSequencerStateSnapshot[];
  macros?: OpbridgeMacroStateSnapshot[];
}

/** When a timeline launch lands (OPBridge S306, `quantize` on `/start` and
 *  `/fire`): at once (on the bridge's next ~33 ms tick) or on the next bar line.
 *  `playNow` (S340, `/start` only, `timeline_play_now`): with Live stopped, at once
 *  on the run's own clock at Live's last tempo, onto Live's grid when it plays. */
export type TimelineQuantize = 'instant' | 'nextBar' | 'playNow';

/** GET /timelines/{consumer}/state — byte-verified field names against
 *  `opbridge_timelines::TimelineStateSnapshot` (serde camelCase). Action-tier
 *  instances expose presence only (`activeActionIds`), no playhead — only
 *  the Song tier has a position to report, since at most one Song runs at
 *  a time. */
/** What `jumpToBar` answers. With `quantize: "instant"` (OPBridge S329, `timeline_jump_instant`): where it lands,
 *  `landingBar` (fractional: the run keeps the clock's bar grid, so it lands at the clock's in-bar phase, in whichever
 *  of `bar` / `bar - 1` puts `bar`'s line nearest) at clock beat `atBeat`; or `quantize: "nextBar"` when it could not
 *  jump instantly (not landed yet, paused, held at a section end, clock stopped) and made the bar-line jump instead. */
export interface JumpToBarAnswer { ok: boolean; quantize?: 'instant' | 'nextBar'; landingBar?: number; atBeat?: number }

export interface TimelineStateSnapshot {
  activeSongId: string | null;
  songPlayheadBar: number | null;
  songSectionId: string | null;
  songIsPlaying: boolean;
  activeActionIds: string[];
  /** OPBridge S323 (`timeline_state_anchor`, asked 2026-10-16), all optional, present while a song timeline is active.
   *  "queued" also while anchored on a line the clock has not reached; `songIsPlaying` stays true while queued. */
  songState?: 'queued' | 'playing' | 'paused';
  /** The clock beat and 0-based timeline bar the run is anchored on: bar = anchorBar + (clockBeat - anchorBeat) / 4
   *  while playing. Absent until anchored. */
  anchorBeat?: number;
  anchorBar?: number;
  /** While queued: the exact clock beat it will anchor on, when one was named (a followed song's bar 1). */
  startLine?: number;
  /** A jump waiting for its bar line: the 0-based bar it lands on. */
  pendingJumpBar?: number;
  /** An INSTANT jump waiting to land (OPBridge S329, `timeline_jump_instant`): the clock beat it lands on, where it
   *  is `pendingJumpBar` (fractional: the clock's bar grid is kept). */
  pendingJumpBeat?: number;
  /** The clock beat and tempo of the engine tick that wrote `songPlayheadBar`. */
  clockBeat?: number;
  bpm?: number;
  /** Play now (OPBridge S340): the run plays on its OWN clock at this tempo while the shared clock is stopped, and
   *  `anchorBeat` / `clockBeat` / `bpm` are that clock's. Absent once it has moved onto the shared clock. */
  freeBpm?: number;
}

// ─── Clock status (musical-time snapshot, Session 0fd) ─────────────────
// GET /clock/status returns OPBridge's canonical MusicalClock snapshot —
// the same shared clock the engines tick loop reads. This is OPButtons's
// clock source post-migration (replaces the legacy Node bridge's WS
// `transport`/`tick` path). snake_case on the wire (Rust default derive,
// no camelCase rename — verified against OPBridge handlers/clock.rs).
//
// Reachable with an `opbuttons-full` bearer: the handler accepts dual auth
// (admin secret OR a Bearer with `/clock/status` scope) and the
// `opbuttons-full` bundle already grants `/clock/status` (OPBridge
// config.rs). No OPBridge work owed — see OPApps/opbridge-opbuttons.md.
export interface ClockStatus {
  /** `"ableton_sync"` (OP-AbletonSync `/sync` markers — the current live
   *  source, verified 2026-07-18), `"ableton_osc"` (older M4L `/oposc/*`
   *  path) or `"ableton_link"`. Additive per OPBridge DEC-430 — treat
   *  unknown values as a generic external source rather than erroring. */
  source: string;
  /** Last-observed Ableton tempo (BPM). */
  tempo: number;
  /** Beat, extrapolated to now when playing + fresh; frozen when stale/
   *  stopped. This is the continuous float — feed it as the anchor beat. */
  beat: number;
  /** 1-indexed bar from the last `/oposc/bar` packet. */
  bar: number;
  /** 1-indexed beat within bar. */
  beat_in_bar: number;
  /** 1-indexed sixteenth within beat. */
  sixteenth: number;
  /** Transport state. False when stale OR explicitly stopped. */
  playing: boolean;
  /** Time-signature numerator (0 if never received). */
  signature_num: number;
  /** Time-signature denominator (0 if never received). */
  signature_den: number;
  /** True when no `/oposc/*` packet in the last 5s OR none ever. */
  stale: boolean;
  /** Milliseconds since the most recent OSC packet. 0 if never. */
  age_ms: number;
  /** True once any `/oposc/*` packet has arrived since boot — lets us
   *  distinguish "Ableton never connected" from "was connected, went
   *  silent". */
  has_ever_received: boolean;

  // ─── OP-AbletonSync device observations (added by OPBridge 2026-08-27) ──
  // All four are OPTIONAL on purpose. `undefined` means "this OPBridge or
  // this device build has not told us", which is a DIFFERENT fact from any
  // real value and must render as `--`, never as a default. In particular
  // `rec_quant`/`launch_quant` label `"0"` is a REAL value meaning None;
  // collapsing absent into None would be a wrong answer wearing the costume
  // of a real one. See abletonRecQuant.ts / abletonLaunchQuant.ts.
  //
  // None of these may be read once and cached: Live hot-swaps a replaced M4L
  // device on refresh with no set reload, so `device_version` and every
  // observation below can change mid-session with no user action we can see.

  /** Build string the OP-AbletonSync device reported, e.g. `"v3.13"`.
   *
   *  ⚠️ DIAGNOSTIC ONLY. Do NOT gate address support on this. OPBridge
   *  withdrew that advice 2026-08-27 on OPM4LBuilder's argument that it is
   *  version archaeology — the same thing OPBridge told OPSetlist not to do
   *  one layer up, and it would make every consumer carry a
   *  version-to-feature table. A failed round-trip verify is the better
   *  signal: it catches a wrong address, an old device AND a dead wire,
   *  where a version check catches only one of the three. Capability
   *  announce is the eventual answer and is deliberately not built yet. */
  device_version?: string;
  /** Live's RECORD quantization as the device reports it. `label` is a
   *  member of `REC_QUANT_LABELS` — a DIFFERENT vocabulary from
   *  `launch_quant` despite sharing most of its text. */
  rec_quant?: ObservedEnum;
  /** Live's GLOBAL LAUNCH quantization. `LAUNCH_QUANT_LABELS`. */
  launch_quant?: ObservedEnum;
  /** Live's AUDIBLE metronome click (`live_set.metronome`) — NOT the visual
   *  beat display. Our phase-strip dots share the word in conversation
   *  only. */
  metronome?: ObservedFlag;
  /** The OP-Quantize devices (one per track) as they last reported
   *  themselves, relayed by OPBridge from `/OP-Quantize/state`. Absent =
   *  never heard (an older OPBridge, or no instance loaded). A song change
   *  from OPSetlist moves these too: OPBridge sends a song's `quant` tag to
   *  `/OP-Quantize/set_grid`, so this is the only place that change shows. */
  op_quantize?: OpQuantizeSnapshot;
  /** OPBridge DEC-685's mesh clock, present only while it is switched ON on this PC (`mesh_clock.rs::status`, read at
   *  OPBridge `47931a2`). Absent = this PC runs its own source, followed by nobody. */
  mesh?: MeshClockStatus;
}

/** `/clock/status.mesh` (OPBridge DEC-685): who this PC follows, or that it leads. */
export interface MeshClockStatus {
  enabled: boolean;
  markedToLead?: boolean;
  /** `following` a leader, `leading` the mesh, or `alone` (on, but no leader yet). */
  role: 'following' | 'leading' | 'alone' | string;
  /** The leader's node id, when there is one. */
  leader?: string | null;
  /** Each PC it hears: its node id and its clock source (`ableton_sync`, `internal`, ...). */
  peers?: Array<{ nodeId: string; source?: string | null; rank?: number | null }>;
  /** While following: whether the leader has gone quiet (this PC then runs on by itself), and the leader's
   *  `timelineLead` when it leads with a timeline (OPBridge S342, follower field; absent from older builds). */
  following?: { leader: string; leaderLost: boolean; timelineLead?: { active: boolean; consumer?: string; timelineId?: string; bpm?: number } };
  /** OPBridge S342 (`timeline_leads_clock`): with Live gone, a timeline started HERE runs the shared clock at its own
   *  tempo and this PC leads the mesh. Reported by the leading PC only; `active: false` otherwise. */
  timelineLead?: { active: boolean; consumer?: string; timelineId?: string; bpm?: number };
}

/** OPBridge's `op_quantize` snapshot (`crates/opbridge-core/src/op_quantize.rs`).
 *  `grid` and `amount` are present only when EVERY instance agrees; absent
 *  with tracks present means the set is mixed, which is not the same as off
 *  (`"0"` is a real label). */
export interface OpQuantizeSnapshot {
  tracks: Array<{ track: string; label: string; amount: number; manual: boolean; version: string; at_unix_ms: number }>;
  grid?: string;
  amount?: number;
  manual_count: number;
  at_unix_ms: number;
}

/** An observed Live enum: Live's own index, the device's label for it, and
 *  when we saw it. **Use the label, never the value** — index 8 is 1/32 in
 *  the record vocabulary and 1/4T in the launch one (OPBridge DEC-545). */
export interface ObservedEnum {
  value: number;
  label: string;
  at_unix_ms: number;
}

/** An observed on/off Live property, with when we saw it. */
export interface ObservedFlag {
  on: boolean;
  at_unix_ms: number;
}

// ─── Mixer feedback (OP-AbletonMixer → OPBridge MixerState, DEC-467) ────
// GET /mixer/status returns Live's per-track mixer state as OPBridge ingested
// it from the OP-AbletonMixer device (device → udpsend 11202 → OPBridge). This
// is the Ableton → iPad feedback half; the control half (iPad → Ableton) rides
// /osc/send. Data-plane, bearer-authed; `opbuttons-full` covers /mixer/status
// (auto-upgraded v4 → v5 on the daemon, no re-pair). snake_case on the wire.
//
// Gotchas the poller MUST honour (see mixer/statusPoll.ts):
//   - `stale: true` is NORMAL (device sends on-change only) — the values are
//     still last-known truth; render regardless, use stale/age for a "synced
//     Xs ago" affordance only.
//   - `tracks.length` may EXCEED `count` (never-truncate) — render first count.
//   - `index` is a 0-based bank position, NOT topology-stable in v0.1.
//   - `has_ever_received: false` = no device ever — show an empty state.
export interface MixerTrackStatus {
  /** 0-based bank position. Not topology-stable in device v0.1 (rebuilds on a
   *  rescan) — appliers key by this absolute index and tolerate remap. */
  index: number;
  /** Track name; may arrive quote-wrapped (Max symbol artifact) — be defensive. */
  name: string;
  /** Normalized volume 0..1. */
  volume: number;
  muted: boolean;
  /** Output meter 0..1 (only streams while the device METERS toggle is on). */
  meter: number;
  // ── v0.2 feedback fields (optional — device v0.1 doesn't emit these; the
  //    poller applies them only when present, so an older daemon is a no-op).
  /** Track solo state. */
  solo?: boolean;
  /** Track record-arm state. */
  armed?: boolean;
  /** Pan -1..1 (0 = center). */
  pan?: number;
  /** Per-send levels 0..1, indexed by send slot. */
  sends?: number[];
  /** Live send-slot count for this track (device `/sendcount` = return count) —
   *  lets the strip right-size instead of the item's fixed `sendCount`. */
  sendCount?: number;
}
export interface MixerStatus {
  count: number;
  ready: boolean;
  /** True when no update in ~5s — NORMAL between changes, not "no data". */
  stale: boolean;
  age_us: number;
  /** False = the device has never connected — render a "no mixer" empty state. */
  has_ever_received: boolean;
  /** Monotonic counter — bumps on any change. Skip re-applying an unchanged snapshot. */
  generation: number;
  tracks: MixerTrackStatus[];
  /** Live master-track state (device v0.2 `/master_*` feedback). Optional — absent
   *  on a daemon that hasn't shipped it; the master strip keys on index -1. */
  master?: { volume?: number; pan?: number; meter?: number };
  /** Live return-track state (device v0.3 `/return_*` feedback). Optional; same
   *  per-strip shape as `tracks` minus arm. The returns bank keys on
   *  `RETURN_BASE + index`. */
  returns?: MixerTrackStatus[];
}

// ─── Stream Deck (Elgato MCP Deck) wire types ─────────────────────────
// Mirrors what the MCP tool `streamdeck__get_executable_actions` reports,
// flattened by OPBridge. Measured against a live Stream Deck 7.4.2 +
// `@elgato/mcp-server` 0.1.1 on 2026-07-28; see OPApps/opbridge-opbuttons.md
// for the raw payload. Every field except `id` is optional on purpose —
// the bridge is v0.1.1 and its shape is expected to churn, so a missing
// title must degrade to a readable fallback, never break the picker.

export interface StreamDeckAction {
  /** MCP action-instance UUID. The identity — this is what gets executed. */
  id: string;
  /** The operator's own title for the key (e.g. "website"). */
  title?: string;
  /** Owning plugin, e.g. `com.elgato.streamdeck.system.website`. */
  pluginId?: string;
  /** Action-type name, e.g. "Website". */
  name?: string;
  /** Action-type description, e.g. "Open URL". */
  description?: string;
}

export interface StreamDeckActionsResponse {
  actions: StreamDeckAction[];
}

export interface StreamDeckExecuteResponse {
  /** `"ok"` on success. NOTE: this is the MCP server's word, and the
   *  retired stub client also said success while doing nothing — treat a
   *  status as "dispatched", not as proof the key fired. */
  status?: string;
}

// ─── Line 6 Helix preset library (DEC-150 / DEC-151) ──────────────────
// OPBridge reads these off the pedal over Line 6's proprietary USB session
// (openhx) — MIDI cannot carry them, Line 6 filter SysEx and publish no name
// query. Read ONCE per session and cached: the read is ~1.3s per setlist
// (~10s for all 8) and, until the session-close packet is found, it also
// stalls the pedal's MIDI Program Changes until a power-cycle. See DEC-150.
//
// `PC = index % 128` — odd setlists report indices 128-255, so `pc` is sent
// by OPBridge pre-normalized and `index` is kept for traceability.
//
// EVERY field but `presets[].pc` and `presets[].setlist` is treated as
// optional: this route does not exist on any shipped OPBridge yet, and a
// consumer that hard-requires a field it has never seen in the wild is how
// the first real response becomes a parse error instead of a picker.

export interface HelixSetlist {
  /** 0-7, and the value sent as CC32 to select it. */
  index: number;
  /** The setlist's name on the unit, e.g. "FACTORY 1". Absent when OPBridge
   *  cannot read setlist names — callers must fall back to "Setlist N"
   *  rather than showing a blank. */
  name?: string;
}

export interface HelixPreset {
  /** 0-7. Which setlist this preset lives in. */
  setlist: number;
  /** Raw index as openhx reported it. **GLOBAL across the whole device**,
   *  0-1023: setlist 0 -> 0..127, setlist 1 -> 128..255, setlist 7 ->
   *  896..1023. NOT a per-setlist 0-255 wrap — OPBridge corrected that
   *  belief (S175) and it is the dangerous kind of wrong: upstream openhx
   *  casts this key to `u8`, so setlist 3's first preset (384) truncates to
   *  128, which is not even a legal 7-bit Program Change. Carried for
   *  auditing only. **NEVER send this — send `pc`.** */
  index?: number;
  /** Program Change number within the setlist, 0-127, derived by OPBridge
   *  from the u16 index. THIS is what goes on the wire. */
  pc: number;
  /** The preset's name on the unit, e.g. "Essex A15". */
  name?: string;
}

/**
 * `GET /obs/scenes` - every scene OBS knows about, plus the active one.
 *
 * Byte-verified against `opbridge-obs::SceneList`'s serialisation
 * (`handlers/obs.rs::list`): `{"current": string|null, "scenes": string[]}`.
 * A plain array of NAMES, not objects - do not reach for `.name`.
 *
 * `current` is nullable on OBS's own side (a genuine program-scene desync),
 * so "no scene is live" is representable and must not be read as "the call
 * failed". Absent/undefined means the same thing as null here.
 */
export interface ObsScenesResponse {
  current?: string | null;
  scenes: string[];
}

/** `POST /obs/scene/{name}` - `{"ok": true, "scene": "<name>"}`. */
export interface ObsSetSceneResponse {
  ok?: boolean;
  scene?: string;
}

/**
 * One entry of `GET /programs` (OPBridge DEC-524, `handlers/program_launch.rs
 * ::list_public`). Byte-checked against the handler's `json!` literal.
 *
 * Deliberately carries NO argv or working directory: OPBridge redacts them
 * from every bearer-readable surface, so a paired device can see WHAT it may
 * run but never HOW. `mode` is how a scoped bearer's press is treated -
 * `instant` spawns, `approve` waits for the operator to OK it on that machine.
 */
export interface ProgramEntry {
  id: string;
  label?: string | null;
  mode: 'instant' | 'approve';
  stream_safe?: boolean;
  state: 'idle' | 'running';
}

/** `GET /programs` - `{"programs": ProgramEntry[]}`. */
export interface ProgramsResponse {
  programs: ProgramEntry[];
}

/**
 * `POST /program/launch/{id}`. Two success shapes, told apart by `status`:
 *
 * - 200 `{"ok": true, "status": "running", "program_id"}` - spawned. The
 *   response IS the spawn ack, not "request accepted".
 * - 202 `{"status": "pending", "request_id", "expires_in_ms",
 *   "long_poll_max_ms"}` - an `approve`-mode program pressed with a bearer:
 *   nothing has run yet, the operator must OK it on that machine.
 */
export interface ProgramLaunchResponse {
  ok?: boolean;
  status: 'running' | 'pending';
  program_id?: string;
  request_id?: string;
  expires_in_ms?: number;
}

/**
 * One knob of a lighting preset (`GET /lighting/presets`, OPBridge DEC-601).
 * Everything a UI needs to draw the control is here: `color` takes
 * `"#rrggbb"`, `choice` takes one of `options` by its label, `level` a
 * number 0..1.
 */
export interface LightingPresetKnob {
  key: string;
  label: string;
  kind: 'color' | 'choice' | 'level';
  default: string | number;
  options: string[];
}

/** One built-in lighting preset. `scope: 'global'` travels across the rig
 *  in room order; `'single'` is meant for one light. Tags include
 *  `photosensitive` on the strobe presets. */
export interface LightingPreset {
  id: string;
  name: string;
  scope: 'global' | 'single';
  description: string;
  tags: string[];
  knobs: LightingPresetKnob[];
}

/** One light's colour in a Hue scene (`level` 0-100), or null where the scene turns it off. */
export type HueSwatch = { rgb: [number, number, number]; level: number } | null;
/** A Hue scene as `GET /hue/scenes` lists it. `moving` = recalling it starts it moving (`auto_dynamic`). */
export interface HueScene { id: string; name: string; moving?: boolean; active?: boolean; lastRecall?: string | null; swatch?: HueSwatch[] }
/** A room or zone holding scenes; `members` are its lights, `swatch` entries in their (name) order. */
export interface HueSceneGroup { id: string; name: string; kind: string; lights?: number; members?: string[]; scenes: HueScene[] }
/** `GET /hue/scenes` (OPBridge S331, PROTOCOL.md "Hue scenes for an app"). */
export interface HueScenesResponse { bridges: Array<{ id: string; reachable?: boolean; groups?: HueSceneGroup[] }> }

/** One scene in `GET /synesthesia/scenes` (OPBridge S344/S352). `mapped: false` = OPBridge has not written a launch for
 *  it into Synesthesia yet, so a press is refused; `picture: false` = no picture to ask for. */
export interface SynesthesiaScene { name: string; own: boolean; mapped: boolean; picture: boolean }
/** `GET /synesthesia/scenes`, answered by the PC that drives Synesthesia (ask any PC). 404 `{error: "not_set_up"}`
 *  when no linked PC has Synesthesia control switched on. `metaControls` and `commands` are read, never assumed: they
 *  changed between S344 and S352 (the mirrors left, `playlistTimer` arrived). */
export interface SynesthesiaScenesResponse {
  installed: boolean;
  enabled?: boolean;
  pc?: string | null;
  nodeId?: string;
  scenes: SynesthesiaScene[];
  playlists: string[];
  homePlaylist?: string | null;
  metaControls?: Array<{ name: string; type: string }>;
  commands?: string[];
}

/** `GET /lighting/presets`. `version` is the catalogue FORMAT version, not a
 *  content version - it does not change when a preset is added. */
export interface LightingPresetsResponse {
  version: number;
  presets: LightingPreset[];
}

/** `POST /lighting/fire` body. These five fields are the WHOLE body: OPBridge
 *  refuses any other field by name (400) rather than ignoring it. */
export interface LightingFireBody {
  id?: string;
  preset: string;
  knobs?: Record<string, string | number>;
  fixtures?: string[];
  lengthBeats?: number;
}

/** `POST /lighting/fire` answer. `lights` names what it is painting
 *  (`colorband-pix-71#3` is zone 3 of a bar); `timeBase` is `clock` when the
 *  musical clock is playing, else `free`. */
export interface LightingFireResponse {
  ok: boolean;
  id: string;
  preset: string;
  lights: string[];
  priority?: number;
  timeBase?: 'clock' | 'free';
  /** OPBridge S297 (v1.15.171): each of this press's lights that someone else holds ("parked"), so it shows nothing
   *  until they let go, e.g. `[{ light: 'back-left', by: 'opstage' }]`; always present, `[]` when none. */
  parked?: Array<{ light: string; by: string }>;
}

/**
 * `POST /opvisual/trigger` (OPBridge S285, v1.15.141; handler
 * `handlers/opvisual.rs`, read 2026-09-28).
 *
 * ⚠️ **`ok: true` is not proof anything happened.** It means OPBridge
 * published the action. `opvisual_connected` says whether an OPVisual socket
 * belonging to the SAME OPBridge user as this bearer was attached to hear it
 * - false when OPVisual is closed, its bridge link is off or awaiting
 * approval, or it is paired under a different user. `sent` is the exact frame
 * OPVisual receives.
 */
export interface OpvisualTriggerResponse {
  ok?: boolean;
  sent?: Record<string, unknown>;
  opvisual_connected?: boolean;
}

/** One cell of OPVisual's `grid` frame (CONTRACT 4c). 1-based `col` / `row`. */
export interface OpvisualGridCell {
  col: number;
  row: number;
  asset_id?: string;
  label?: string;
  kind?: string;
  playable?: boolean;
  /** A path under `/opvisual` (e.g. `/thumb/<id>`), or null: no picture. */
  thumb?: string | null;
}

export interface OpvisualGridFrame {
  cols?: number;
  rows?: number;
  version?: string;
  cells?: OpvisualGridCell[];
}

/** `GET /opvisual/state`. MEASURED 2026-10-06 against OPBridge on the dev PC:
 *  the grid frame sits under `grid.frame` (`{at_unix_ms, frame}`), not
 *  directly under `grid` as the ask described; both are read. */
export interface OpvisualStateResponse {
  connected?: boolean;
  grid?: (OpvisualGridFrame & { frame?: OpvisualGridFrame; at_unix_ms?: number }) | null;
  state?: unknown;
  note?: string;
}

/**
 * `GET /setlist/state` - what OPSetlist last told OPBridge, and what
 * OPBridge actually sent on to Live (OPBridge DEC-585, scope bundle v17).
 *
 * ⚠️ **A `null` field means "this song carried no value for that leg, so
 * Live was left alone" - NOT zero, and NOT unknown.** All four legs are
 * independently conditional and an absent field is never sent, which makes
 * every value STICKY across songs. `current` is this song's INTENT;
 * `last_sent` is what Live actually received. See `setlist/state.ts`.
 */
export interface SetlistCurrent {
  artist?: string | null;
  song_title?: string | null;
  bpm?: number | null;
  time_sig?: string | null;
  quant?: string | null;
  /** Semitones. `null` = this song asked for no transpose. */
  keys_trans?: number | null;
  /** e.g. `"Bbm"`. `null` = this song asked for no key. */
  vocals_key?: string | null;
}

/** What OPBridge last actually SENT to Live. **Deliberately has no
 *  `vocals_key`** - Waves state is not queryable by anything, so there is no
 *  "what Live actually has" for it to report. That ceiling is structural. */
export interface SetlistLastSent {
  at_unix_ms?: number | null;
  bpm?: number | null;
  time_sig?: string | null;
  quant?: string | null;
  keys_trans?: number | null;
}

/** What OPBridge's autotune leg did with the last NON-EMPTY `vocals_key` it
 *  was handed (`setlist.rs::AutotuneOutcome`, OPBridge S277/S279, read
 *  2026-09-28). Written at the moment of the act by `send_autotune`, never by
 *  `mark_sent`, and NOT written for a song that carried no key - so it is
 *  sticky across keyless songs, exactly like the plugin. In memory only: null
 *  after every OPBridge restart until the next keyed song. */
export interface SetlistAutotuneOutcome {
  /** The key as OPSetlist sent it, after OPBridge's Rust `trim()`. */
  vocals_key: string;
  /** OPBridge's own parse. Absent when it could not parse the key - in which
   *  case nothing was ever sendable. Both-or-neither with `mode`. */
  pitch_class?: number;
  /** `"major"` | `"minor"` | `"chromatic"`. */
  mode?: string;
  /** Both CCs LEFT on the configured port. Does not claim Waves heard them;
   *  nothing can. `false` = parsed-but-no-port or unparseable. */
  emitted: boolean;
  at_unix_ms: number;
}

/** `/setlist/state`'s `vocals_key` block. An OBJECT, unlike
 *  `current.vocals_key`, which is the raw string the song carried. */
export interface SetlistVocalsKeyState {
  /** An autotune MIDI port is set in OPBridge's rig config. */
  configured?: boolean;
  last?: SetlistAutotuneOutcome | null;
}

export interface SetlistStateResponse {
  current?: SetlistCurrent | null;
  last_sent?: SetlistLastSent | null;
  /** See {@link SetlistVocalsKeyState}. Absent from an OPBridge older than
   *  S279 (v1.15.132). */
  vocals_key?: SetlistVocalsKeyState | null;
  /** Monotonic write counter. `0` means nothing has ever been written.
   *  The `watch` channel coalesces, so this - not arrival order - is how a
   *  poll response and a push frame are sequenced against each other. */
  generation?: number;
  auto_sync?: boolean;
  link_enabled?: boolean;
  /** `current` was forwarded to Live by THIS bridge. `false` for a held song
   *  (auto-sync off, the operator scrolling) and for one adopted from a peer. */
  sent?: boolean;
  /** `null` when OPSetlist sent `current` to this bridge; the peer's node id
   *  when it was adopted from the mesh (OPBridge S289). */
  current_origin_node_id?: string | null;
  /** On a song ADOPTED from a peer (OPBridge S324, our CP-194 ask): whether the ORIGIN sent it to Live, and the
   *  origin's last autotune outcome. `null`/absent for a song sent to this bridge, and from a peer on an older build. */
  origin_sent?: boolean | null;
  origin_last_autotune?: SetlistAutotuneOutcome | null;
  observed_rec_quant?: string | null;
}

/** One `/devices/status` entry - a device QUERY and how it resolved, not a
 *  device. Field names are OPBridge's (`ableton_devices.rs::DeviceEntry`) and
 *  are deliberately left in their wire spelling here; `ableton/deviceState.ts`
 *  is the single place that renames them into our own shape. */
export interface DevicesStatusEntry {
  /** Track as the device named it. OPBridge accepts an int index or a name
   *  and normalises both to a string. */
  track: string;
  /** The device-name filter this entry is keyed on, `san()`-ed by the device
   *  (so never spaced - see `ableton/deviceControl.ts::deviceKey`). */
  name: string;
  /** ⚠️ 1-BASED, despite OPBridge's own field doc saying "0-based". The
   *  device clamps `nth` to >= 1 before it emits (`OP-ButtonsDevice.js`
   *  :340) and indexes `hits[nth - 1]`, so every real emission carries 1 or
   *  more. OPBridge passes the value straight through without interpreting
   *  it, so the wire is 1-based and only their comment is wrong; the `0`s in
   *  their worked example are synthetic-probe payloads. Flagged to them. */
  nth: number;
  state: 'resolved' | 'no_match' | 'ambiguous';
  /** Present ONLY on `resolved` - omitted, never `false`, otherwise. */
  on?: boolean;
  /** Present only on `ambiguous`. */
  count?: number;
  at_unix_ms: number;
}

/**
 * `GET /devices/status` - Ableton device on/off readback (OPBridge DEC-584).
 *
 * Two fields answer different questions and both matter: `has_ever_received:
 * false` is *"the device has never spoken"*, while `stale: true` is *"it
 * spoke, then went quiet"* (5s threshold). A stale snapshot keeps its last
 * known values rather than emptying, so an empty `devices` must not be read
 * as "the device is absent".
 */
export interface DevicesStatusResponse {
  devices: DevicesStatusEntry[];
  /** LIVENESS - the M4L device said `alive` and has not gone quiet. NOT
   *  derived from entry stamps, and a heartbeat does not refresh them. */
  alive: boolean;
  device_version?: string;
  stale: boolean;
  age_us: number;
  has_ever_received: boolean;
  generation: number;
}

/** `GET /helix/presets` status. Never-probed is a **200**, never a 404 — the
 *  same honesty rule as `/streamdeck/actions`: "no names yet" and "endpoint
 *  not built" must not collapse into one another. */
export type HelixReadStatus = 'never_probed' | 'ok' | string;

export interface HelixPresetsResponse {
  status?: HelixReadStatus;
  /** e.g. "Helix Floor". Informational. */
  device?: string;
  /** On the never-probed response: the pedal IS visible, we just have not
   *  read it. This is what separates "no names yet" from "no pedal". */
  device_present?: boolean;
  /** OPBridge's own next-step string on the never-probed response. */
  hint?: string;
  setlists_read?: number;
  read_at_unix?: number;
  duration_ms?: number;
  /** NOT in OPBridge's shipped shape — setlist NAMES were asked for after
   *  they built this. Optional so the picker works either way. */
  setlists?: HelixSetlist[];
  presets: HelixPreset[];
}

/** `POST /helix/probe` — the ~8s USB read (1024 presets across 8 setlists,
 *  measured 7.9s on the operator's Floor). Its response deliberately reports
 *  its own cost rather than a clean success. */
export interface HelixProbeResponse {
  status?: string;
  setlists_read?: number;
  presets_read?: number;
  duration_ms?: number;
  /** True when the read has left the pedal ignoring MIDI Program Changes.
   *  MUST be surfaced — a silent `true` means the operator discovers it when
   *  a preset button stops working mid-set. */
  midi_program_changes_stalled?: boolean;
  /** OPBridge's wording of the recovery (replug USB, or open+quit HX Edit). */
  note?: string;
}

/** `GET /helix/status` — enumeration only, opens no USB session, safe to
 *  poll. Every field optional; treat as advisory. */
export interface HelixStatusResponse {
  device_present?: boolean;
  device?: string;
  status?: HelixReadStatus;
  presets_cached?: number;
  read_at_unix?: number;
}

/** `GET /midi/ports` — the live host port enumeration. Shipped on OPBridge
 *  since v1.15.13 (`opbuttons-full` v6); OPButtons never grew a typed client
 *  for it until the guitar-pedal registry needed to auto-detect a Helix's
 *  port by name rather than asking the operator to type it. */
export interface MidiPortsResponse {
  outputs: string[];
  inputs?: string[];
}

/** The REAL wire shape, confirmed live against the dev PC OPBridge
 *  2026-08-03: `{index, name}[]`, not the bare `string[]` this file had
 *  assumed since `MidiPortsResponse` was first typed. Every consumer
 *  (`detectHelixPort`, the Custom-pedal port `<select>`) reads plain
 *  names, so `midiPorts()` normalizes here rather than pushing the `.name`
 *  unwrap onto every caller — a bare cast to `MidiPortsResponse` would have
 *  let `outputs[i].toLowerCase is not a function` reach the operator the
 *  first time they pressed "Detect" against a real bridge. */
interface RawMidiPortsResponse {
  outputs: (string | { name: string })[];
  inputs?: (string | { name: string })[];
}

function namesOf(entries: (string | { name: string })[] | undefined): string[] | undefined {
  return entries?.map((e) => (typeof e === 'string' ? e : e.name));
}

// ─── Pair-once-forever wire types (v1.14+, DEC-060) ────────────────────

export interface PairingConsumerEntry {
  id: string;
  display_name: string;
  default_scope_bundle_id: string;
}
export interface PairingScopeBundleEntry {
  id: string;
  consumer: string;
  name: string;
  version: number;
}
export interface PairingConsumersResponse {
  consumers: PairingConsumerEntry[];
  scope_bundles: PairingScopeBundleEntry[];
}

/** Response from `POST /midi/panic` (OPBridge data-plane global panic).
 *  `ports_hit` is how many output ports the all-notes-off reached; `errors`
 *  carries per-port failure strings and is empty on a clean run. */
export interface MidiPanicResponse {
  ok: boolean;
  ports_hit: number;
  errors: string[];
}

/** Response from `POST /engines/{consumer}/stopAll`. `total` is the sum of
 *  the per-family counts — 0 is the normal case when nothing was running,
 *  NOT a failure. */
export interface StopAllEnginesResponse {
  ok: boolean;
  stopped: { lfos: number; sequencers: number; macros: number };
  total: number;
}

export interface PairingRequestBody {
  consumer: string;
  user_id?: string;
  fan_out?: boolean;
  requested_bundle_id?: string;
  /** Stable per-device id (DEC-468). Lets OPBridge replace this device's own
   *  prior token on re-pair instead of stacking a new one. Optional + additive:
   *  absent = pre-DEC-468 behaviour (no de-dup). See storage.deviceId(). */
  device_id?: string;
}
export interface PairingRequestOpenedResponse {
  request_id: string;
  expires_in_ms: number;
  long_poll_max_ms: number;
}

export type PairingRequestStatus = 'pending' | 'approved' | 'denied' | 'expired';
export interface PairingPeerRow {
  instance_name: string;
  bridge_url: string;
  bearer: string | null;
  status: 'ok' | 'error' | 'pending' | 'timeout';
  error?: string | null;
}
export interface PairingRequestStatusResponse {
  request_id: string;
  status: PairingRequestStatus;
  consumer: string;
  user_id: string;
  bundle_id: string | null;
  bundle_version: number | null;
  local_bearer: string | null;
  /** OPBridge's own `instance_name` when the request resolves — the PWA
   *  stores this on the freshly-added row so subsequent boots can key
   *  by identity rather than URL. Absent while `status === "pending"`. */
  instance_name?: string | null;
  peers: PairingPeerRow[];
}

export interface PairedBridgeEntry {
  instance_name: string;
  bridge_url: string;
  display_name: string;
  /** Stable node identifier of this peer bridge (from its mDNS TXT
   *  `node_id`). Added by OPBridge v1.15.8 (S148, DEC-465) as the
   *  address-resolvable join key: an inheritance carries the minting
   *  bridge's `node_id`, and the consumer joins it against THIS field to
   *  resolve the bridge's `host:port`. Empty on pre-v1.15.8 daemons — the
   *  inheritance-drainer treats an empty key as unresolvable and falls back
   *  to the manual pairing flow. Do NOT join on `instance_name` (unstable
   *  mDNS label). */
  node_id?: string;
  /** The peer's suite-cert hostname (`<hash>.bridge.overpoweredapps.net`) and
   *  its browser TLS port. OPTIONAL and, as of 2026-09-07, NOT YET SENT by
   *  OPBridge - asked for that day, and inert until it lands.
   *
   *  Why it matters: from the hosted HTTPS origin (an iPad on
   *  `opbuttons.overpoweredapps.net`) the `bridge_url` above is a plain
   *  `http://<lan-ip>:8765` and is MIXED-CONTENT BLOCKED, so a peer cannot be
   *  probed at all and therefore cannot be auto-adopted. Measured 2026-09-07:
   *  `https://<suite_hostname>:8768/health` answers 200 with a VALID cert,
   *  while the derivable `https://<lan-ip>:8768` is cert-rejected because the
   *  leaf does not cover the bare IP. So the hostname is the only route, and
   *  only the mesh can supply it - the peer's own /health is unreachable from
   *  there, which is the chicken-and-egg this field breaks. */
  suite_hostname?: string;
  browser_tls_port?: number;
}
export interface PairedBridgesResponse {
  bridges: PairedBridgeEntry[];
}

// ─── Pairing inheritances (Phase B silent-push resync + revocation probe) ──
// A peer bridge minting a bearer for this (user_id, consumer) surfaces as an
// "inheritance". The /ws/consumer `inheritance_available` push delivers new
// mints live (opbridge/consumerWs.ts); this GET is the reconnect-resync
// backfill AND  -  because OPBridge ships NO `bearer_revoked` push (verified
// 2026-07-18: the daemon only emits `inheritance_available` + `clock_marker`)
//  -  the out-of-band revocation probe: a revoked/invalid bearer returns 401
// (`OpbridgeError` kind 'unauthorized'), which the consumer-WS client reads as
// "this device's access is gone -> re-pair".
//
// Wire notes: the bearer rides as a QUERY PARAM (not the Authorization header)
// so a plain browser fetch authenticates; `since` is a uss-since-epoch cursor
// (matches `minted_at_us`), strictly-greater-than filtered. Field-name skew vs
// the WS event: the poll returns `bridge_instance_name`; the WS push renames
// the same datum to `instance_name`.
export interface PairingInheritance {
  /** mDNS instance name of the peer bridge that minted this bearer. (The WS
   *  `inheritance_available` event calls the same field `instance_name`.)
   *  UNSTABLE display label — never use it as the resolution key; use
   *  `node_id`. */
  bridge_instance_name: string;
  /** Stable node_id of the bridge that minted this bearer. The
   *  address-resolvable join key (DEC-465): the drainer joins it against
   *  `GET /peers/paired-bridges[].node_id` to find the bridge's `host:port`,
   *  then uses `bearer` against it — that is how a bearer for a bridge this
   *  device has NEVER contacted (a new/reinstalled mesh bridge) becomes
   *  usable with no re-pair. Empty string on pre-v1.15.8 daemon records
   *  (`#[serde(default)]` OPBridge-side) — the drainer skips those (they
   *  predate auto-mint and are unresolvable). Added v1.15.8 (S148). */
  node_id: string;
  /** The full `opb_...` bearer for that peer bridge  -  store as the credential. */
  bearer: string;
  consumer: string;
  user_id: string;
  scope_bundle_id: string;
  scope_bundle_version: number;
  /** uss since UNIX epoch  -  advance the `?since=` cursor to the max seen. */
  minted_at_us: number;
  /** Internal server field; effectively always 0 here  -  ignore. */
  since_us?: number;
  /** DEC-568: the first 10 chars of the token this mint REPLACED on the minting
   *  bridge, when it replaced one. Carried by the GET; the WS push omits it
   *  (OPBridge `consumer_ws.rs`, read 2026-11-03). The drainer adopts an
   *  inheritance for a row it holds only when this names that row's key, or
   *  the held key is proven dead (audit B-6). */
  supersedes?: string | null;
}
export interface PairingInheritancesResponse {
  inheritances: PairingInheritance[];
  server_now_us: number;
}

/** One inbound MIDI envelope from OPBridge's traffic-monitor ring
 *  (`crates/opbridge-core/src/traffic_monitor.rs::MidiTrafficEntry`,
 *  camelCase on the wire). `seq` is per-stream monotonic, resets on
 *  daemon restart (ring is in-memory only) — used to de-dupe the REST
 *  backfill against the WS push tail, not as a durable id. */
export interface TrafficMidiEntry {
  seq: number;
  port: string;
  timestampUs: number;
  bytes: number[];
  filtered: boolean;
}

/** One inbound OSC envelope — mirrors `OscTrafficEntry`. `argTypes` is a
 *  compact type-tag string ("if" = int+float), not the argument values —
 *  v1 surfaces "what arrived", not "what was inside" (OPBridge's own
 *  doc comment). */
export interface TrafficOscEntry {
  seq: number;
  source: string;
  timestampUs: number;
  address: string;
  argTypes: string;
  filtered: boolean;
}

/**
 * One entry from `GET /midi/recent` — OPBridge's inbound-MIDI ring.
 *
 * ✅ BYTE-VERIFIED LIVE 2026-08-27 against a real Midi Fighter Twister on the
 * dev-PC daemon, replacing the prose-derived guess this type used to carry.
 * A captured encoder turn reads exactly:
 *
 *   {"port":"Midi Fighter Twister","timestamp_us":31980228711,
 *    "bytes":[183,64,83],"channel":7,"status":176,"data1":64,"data2":83}
 *
 * ⚠️ **`status` is ALREADY MASKED and `channel` is a SEPARATE field.**
 * OPBridge's `parse_channel_voice` splits the first byte before serialising
 * (`status = first & 0xF0`, `channel = first & 0x0F` — opbridge-midi
 * `lib.rs`), so the sample above shows `bytes[0] = 183` (0xB7) arriving as
 * `status: 176` (0xB0) + `channel: 7`. Re-deriving the channel with
 * `status & 0x0f` therefore yields **0 for every message on every channel** —
 * a real bug this type's earlier prose invited, and which had shipped in
 * `midiTrigger/learn.ts` until this verification.
 *
 * The optional fields mirror OPBridge's own `Option<u8>` +
 * `skip_serializing_if`: a non-channel-voice message (sysex, real-time)
 * carries none of them, so they are genuinely absent rather than zero.
 */
export interface MidiRecentEntry {
  port: string;
  /** Microseconds since daemon start (monotonic), not a wall clock. */
  timestamp_us?: number;
  /** Raw bytes exactly as received — channel nibble still in `bytes[0]`. */
  bytes?: number[];
  /** 0-based MIDI channel, already split out of the status byte. */
  channel?: number;
  /** Status with the channel nibble MASKED OFF (0x80/0x90/0xB0/0xC0...). */
  status?: number;
  data1?: number;
  data2?: number;
}

/** `GET /midi/recent` response envelope. ⚠️ The ring is wrapped in
 *  `{ messages: [...] }` — it is NOT a bare array, unlike
 *  `/traffic/midi/recent` which genuinely is. Conflating the two yields an
 *  object where the caller expects an array, and `arr.length` then reads
 *  `undefined` instead of throwing, so the failure is silent. */
export interface MidiRecentResponse {
  messages: MidiRecentEntry[];
}

// ─── OPBridge rules engine wire types (DEC-154, DEC-469/470) ────────
// Confirmed against `opbridge-rules/src/rule.rs` (camelCase on the wire) —
// see the DEC-154 design notepad's "RESOLVED" section for the derivation.

export interface RuleLeafPredicate {
  /** `oscAddressEquals` (2026-10-24, the OP-Motion capture answer): an OSC message on OPBridge's input whose address
   *  is exactly `address` (`opbridge-rules/src/rule.rs:120`). */
  op: 'noteOn' | 'noteOff' | 'noteEquals' | 'ccEquals' | 'channelEquals' | 'portEquals' | 'oscAddressEquals';
  value?: number;
  number?: number;
  name?: string;
  address?: string;
}

export interface RuleConditionGroup {
  op: 'and' | 'or' | 'not';
  children?: RuleCondition[];
  child?: RuleCondition;
}

export type RuleCondition = RuleLeafPredicate | RuleConditionGroup;

// ─── Controller codes (OPBridge S301/S307/S309, PROTOCOL.md "Controller codes") ──

/** A control's kind as OPBridge names it. */
export type ControlKind = 'footswitch' | 'button' | 'knob' | 'fader' | 'expression' | 'wheel';

export interface ControllerControl {
  /** Permanent. What a binding names. */
  id: string;
  /** Renamable, e.g. `NP1-P3`. For people, never for binding. */
  code: string;
  name?: string;
  kind: ControlKind | string;
  /** Press and release arrive together (the SoftStep). */
  tap?: boolean;
  layoutId?: string;
}

export interface ControllerUnit {
  id: string;
  code: string;
  name?: string;
  model?: string;
  modelName?: string;
  /** Added on its own when plugged in; gains controls as they are used. */
  autoLearn?: boolean;
  ports: Array<{ nodeId: string; name: string; portId?: string }>;
  controls: ControllerControl[];
}

export interface ControllerPage {
  id: string;
  name?: string;
  order?: number;
  fallsThroughTo?: string;
  song?: string;
}

export interface ControllerBinding {
  pageId: string;
  controlId: string;
  actionId: string;
  releaseActionId?: string;
  input?: string;
  holdMs?: number;
  onEveryPage?: boolean;
}

export interface ControllerRegistry {
  units: ControllerUnit[];
  pages: ControllerPage[];
  bindings: ControllerBinding[];
  activePageId?: string;
}

/** A binding write. No `actionId` removes the binding. */
export interface ControllerBindingWrite {
  pageId: string;
  controlId: string;
  actionId?: string;
  releaseActionId?: string;
  input?: string;
}

/** OPBridge `opbridge-protocol::actions::NamedAction`, the `Dispatch` form
 *  only (the one this app registers). Read at their source 2026-09-23:
 *  `payload` is `#[serde(tag = "kind")]`, so `kind` sits BESIDE the fields
 *  rather than wrapping them; `consumer` is REQUIRED (the
 *  routing key, and DEC-597 refuses a dispatch aimed at another app);
 *  `user_id` is left out so OPBridge resolves it at fire time. */
/**
 * A named action as `PUT /admin/actions/{id}` takes it (OPBridge
 * `NamedActionPayload`, externally tagged on `kind`): replay ONE MIDI send,
 * ONE OSC send, or ONE rules-engine command. A mod fires it by id and never
 * supplies bytes, an address or a target.
 */
export type NamedActionStep =
  | { kind: 'midi'; port: string; bytes: number[] }
  | { kind: 'osc'; address: string; args: unknown[]; target: string }
  | { kind: 'dispatch'; module: string; command: string; args: Record<string, unknown>; consumer: string }
  /** PROPOSED 2026-09-29: replay one of OPBridge's own routes, bounded by the
   *  registering bearer's scopes. `/health.capabilities.named_action_route`. */
  | { kind: 'route'; method: 'POST' | 'PUT' | 'DELETE'; path: string; body: unknown };

export type NamedActionPayload =
  | NamedActionStep
  /** PROPOSED 2026-09-29: several steps in order.
   *  `/health.capabilities.named_action_sequence`. */
  | { kind: 'sequence'; steps: NamedActionStep[] };

/**
 * A value a mod may supply when firing (faders, XY, rotaries, levels).
 *
 * PROPOSED to OPBridge 2026-09-29, not yet built there: the registered
 * payload holds a real default number at `at` (a JSON pointer into
 * `payload`, e.g. `/bytes/2` or `/args/0`), and `POST /actions/{id}/fire`
 * with `{ "values": { "<name>": n } }` clamps `n` to [min, max] (rounded when
 * `integer`) and writes it there before replaying. The mod still never
 * supplies anything but numbers. OPButtons registers an action carrying
 * `inputs` ONLY on a bridge advertising `/health.capabilities
 * .named_action_inputs === true`: an older OPBridge would silently drop the
 * field and register a fixed press at the default, a share that looks fine
 * and does the wrong thing.
 */
export interface NamedActionInput {
  name: string;
  /** Every place the value lands - several for a light level across
   *  several fixtures in one sequence. */
  at: string[];
  min: number;
  max: number;
  integer?: boolean;
}

export interface NamedAction {
  id: string;
  label?: string;
  payload: NamedActionPayload;
  inputs?: NamedActionInput[];
}

export interface RuleAction {
  module: string;
  command: string;
  args: Record<string, unknown>;
  delayMs?: number;
}

export interface Rule {
  id: string;
  priority: number;
  stopAfterMatch?: boolean;
  condition: RuleCondition;
  actions: RuleAction[];
  enabled?: boolean;
  name?: string;
  description?: string;
}

/** ⚠️ `userId` and `version` are REQUIRED, corrected 2026-08-28 after finding
 *  that every rule-set push this project has ever made was rejected 422.
 *
 *  They were typed optional here, `buildRuleSet` therefore omitted them, and
 *  `ruleSync.ts` swallowed the resulting error in a bare `catch {}` - so the
 *  whole DEC-154 A-path (Sequencer/Macro/LFO/Panic bindings) has been
 *  authoring bindings, badging them "A - fires even with the tab closed", and
 *  never arming a single rule. Verified against the live dev-PC daemon
 *  (v1.15.91): `{consumerId, rules}` → 422 `missing field userId`, then 422
 *  `missing field version`, then 200 once both are present; and that user's
 *  on-disk `rules/` dir holds only another consumer's `opstream.json`, no
 *  `opbuttons.json` has ever been written.
 *
 *  Matches OPBridge's `opbridge-rules::rule::RuleSet`, where `user_id` and
 *  `version` are plain non-`Option` fields with no `#[serde(default)]` - they
 *  were never optional, so this type was wrong from the day it was written
 *  rather than drifting later. */
export interface RuleSet {
  consumerId: string;
  /** OMIT THIS. Optional since OPBridge `v1.15.92+6e8dd4f` (S242, shipped the
   *  same day we reported it): absent means "the authenticated token's user",
   *  which is the only value a consumer could correctly supply anyway. A
   *  NON-EMPTY value must still match the token or OPBridge 403s, so sending
   *  one can only ever fail - `buildRuleSet` deliberately does not.
   *
   *  Their reasoning, worth keeping because it explains why this is the
   *  permanent shape rather than a workaround: `get`, `delete` and the event
   *  log in their `handlers/rules.rs` all already read `token.user_id` and
   *  never ask the client. The push path was the lone outlier. */
  userId?: string;
  /** REQUIRED - still 422s without it on v1.15.92 (re-probed, not assumed
   *  when `userId` was relaxed). Monotonic per (consumer, user); OPBridge
   *  uses it for hot-reload version detection. */
  version: number;
  rules: Rule[];
  variables?: Record<string, unknown>;
}

// ─── Test tracks (shapes posted to OPBridge 2026-10-13) ─────────────────
export interface TestTrack {
  id: string;
  name: string;
  durationSec: number;
  analysed: boolean;
  /** Present while file sync is still bringing it to this PC. */
  arriving?: { pct: number };
  /** True while OPBridge's helper analyses it (OPBridge S318). */
  analysing?: boolean;
  /** Why the analysis failed (e.g. "no track analyser on this PC"). */
  analysisError?: string;
  /** The audio library (asked of OPBridge 2026-10-13, capability `audio_library`): what the operator says it is
   *  (absent or null = not said: OPButtons guesses), its size in bytes (OPBridge sends `size`, read on the dev PC
   *  2026-10-14; this said `sizeBytes`, the name we asked for, so the Audio section never showed a size), and whether
   *  it waits for its turn to be analysed. */
  kind?: AudioKind | null;
  size?: number;
  queued?: boolean;
}
/** A file's type in the audio library: a one-shot sound, a loop, or a song. */
export type AudioKind = 'oneshot' | 'loop' | 'song';
export interface TestTrackPeaks { ms: number; min: number[]; max: number[] }
export type TestTrackTempoChoice = 'as-read' | 'half' | 'double';
/** A song stretch marked with a timeline section's name. Bars from bar 1 on the chosen tempo, 1-based, inclusive. */
export interface TestTrackSectionMark { name: string; fromBar: number; toBar: number }
/** The helper's results file (snake_case, a com.opapps.beatmap v1 superset) plus the operator's camelCase fields. */
export interface TestTrackBeatmap {
  v: number; bpm: number; sig: string; beats: number;
  duration_sec: number; bar1_sec: number;
  /** `[downbeat_sec, bpm]` pairs after the first segment, as the analyser writes them (OPCut tempo_map.py). */
  alt_bpms?: number[]; tempo_changes?: [number, number][];
  warnings?: string[];
  hits?: { kick: { t: number; v: number }[]; snare: { t: number; v: number }[]; hats: { t: number; v: number }[] };
  vocals?: { start: number; end: number }[];
  bar_energy?: number[];
  /** How sure the beat tracker is of the grid, 0..1 (low = no steady beat). */
  confidence?: number;
  tempoChoice?: TestTrackTempoChoice;
  sections?: TestTrackSectionMark[];
}
export interface TestTrackBeatmapPatch { bar1Sec?: number; tempoChoice?: TestTrackTempoChoice; sections?: TestTrackSectionMark[] }
export interface TestTrackPlay { id: string; pc: string; output: string; fromBar?: number; loop?: { fromBar: number; toBar: number } }
export interface TestTrackStatus {
  playing: boolean; id?: string; pc?: string; output?: string;
  bar?: number; beat?: number; bpm?: number; posSec?: number; bar1Sec?: number;
  tempoChoice?: TestTrackTempoChoice; loop?: { fromBar: number; toBar: number };
}
export interface AudioOutput {
  id: string; name: string; default?: boolean;
  /** How late this output really is (ms), measured or set; test tracks and pads allow for it (OPBridge S318). */
  latencyMs?: number;
}

// ---- audio pads (OPBridge S318, capability `audio_pads`, PROTOCOL.md "Audio pads"): a soundboard that plays files
// from the song library on a PC's output DURING the show, several at once, launched now / on the beat / on the bar.
export type PadMode = 'oneshot' | 'loop' | 'gate';
export type PadQuant = 'now' | 'beat' | 'bar';
export type PadWarp = 'off' | 'repitch';
export interface PadPlay {
  padId: string; trackId: string; pc?: string; output?: string;
  mode?: PadMode; quant?: PadQuant; gain?: number; fadeMs?: number; startSec?: number; endSec?: number;
  warp?: PadWarp; bpm?: number;
}
export interface PadPlayAnswer {
  ok: boolean; output?: string; pc?: string; muted?: boolean; rate?: number;
  quant?: { asked: PadQuant; atBeat?: number | null; inMs?: number | null; fellBack?: string | null };
}
export interface PadVoice {
  padId: string; trackId: string; mode: PadMode; state: 'waiting' | 'playing' | 'releasing';
  posSec: number; startSec?: number; endSec?: number; progress: number; rate?: number | null; lateMs?: number | null; output?: string;
}
export interface PadsStatus {
  pc?: string; level?: number; muted?: boolean;
  outputs?: Array<{ output: string; open: boolean; error?: string | null }>;
  voices: PadVoice[];
}
export interface PadsSettings { level?: number; muted?: boolean }
/** `POST /timelines/{consumer}/start`'s answer. `testTrack` is there when the timeline brings its song (OPBridge
 *  S318, `timeline_test_track`): playing, or why not. The timeline plays either way. */
export interface TimelineStartAnswer {
  ok: boolean;
  testTrack?: {
    playing: boolean; id?: string; output?: string;
    /** ableton_playing | arriving | not_analysed | not_found | performance_mode | output | other_pc */
    reason?: string;
    message?: string;
  };
  /** OPBridge S342: this press made the timeline lead the clock (Live gone), at this tempo. */
  timelineLeads?: { bpm: number };
}

/** How long a failed fetch sends header-carrying PUTs straight to WebTransport (`OpbridgeHttpClient.request`): long
 *  enough that a gig offline pays fetch's failure once a minute at most, short enough that a network coming back is
 *  used again soon. */
const FETCH_DOWN_MS = 60_000;

export class OpbridgeHttpClient {
  constructor(
    private readonly baseUrl: string,
    private readonly bearer: string | null,
    private readonly defaultTimeoutMs = 3000,
    /** Offline-capable transport (2026-08-31). When supplied, every request
     *  goes over WebTransport to a pinned self-signed LAN endpoint instead of
     *  `fetch` - no DNS, no CA, so it survives a venue with no internet.
     *  Supplied by `store.ts` once a bridge has advertised `/health
     *  .webtransport`; absent on a daemon too old to advertise it, on a
     *  browser without WebTransport, and on the first boot before the advert
     *  has been learned. In every one of those cases `fetch` runs exactly as
     *  it always has. See wtTransport.ts and OFFLINE-LAN-RESEARCH.md. */
    private readonly wt: WtConnection | null = null,
  ) {}

  /** The WebTransport session backing this client, if any. Diagnostics only -
   *  callers must not issue requests through it directly, or they bypass the
   *  fetch fallback that makes the path safe. */
  wtSession(): WtConnection | null { return this.wt; }

  /** GET /health — no auth required (localhost-only, but the PWA hits
   *  via LAN IP). Used as the liveness probe by the connection manager.
   *  `timeoutMs` lets the connection manager apply its own tight probe
   *  budget (`HEALTH_TIMEOUT_MS`, connection.ts) to THIS call specifically,
   *  without that budget becoming the client's `defaultTimeoutMs` for
   *  every other request issued through the same connection (2026-08-23
   *  latency pass - see `makeConnection()`'s doc comment). */
  health(timeoutMs?: number): Promise<OpbridgeHealth> {
    return this.request<OpbridgeHealth>('GET', '/health', undefined, { auth: false, ...(timeoutMs !== undefined ? { timeoutMs } : {}) });
  }

  listFixtures(): Promise<FixturesListResponse> {
    return this.request<FixturesListResponse>('GET', '/fixtures');
  }

  /** GET /dmx/profiles - the vendored fixture make/model catalogue. Build-time
   *  const data on OPBridge, so it changes only when the daemon updates; read
   *  on connect rather than polled, same cadence as `/fixtures` (DEC-131). */
  listDmxProfiles(): Promise<DmxProfilesResponse> {
    return this.request<DmxProfilesResponse>('GET', '/dmx/profiles');
  }

  /**
   * POST /dmx/fixtures/{id}/state - partial write: an omitted field means
   * "leave this alone".
   *
   * THE DEFAULT WRITE for this app, deliberately in preference to `/color`.
   * `/color` forces a whole look with a required `rgb` and an `intensity`
   * that defaults to FULL, so a consumer wanting to change only the level has
   * to fold the level into the RGB magnitude and send a half-brightness
   * colour. That works and is honest about the bytes, but the fixture is then
   * being told a COLOUR when the operator moved a LEVEL fader - OPBridge
   * built this route after OPStage reported exactly that ("colour is the only
   * door, so everything else has to be smuggled through it"). Using it means
   * a level control cannot disturb a colour it was never asked about.
   *
   * `on: false` blacks the wire but REMEMBERS the colour, so a later
   * `on: true` restores it rather than lighting white.
   */
  dmxSetState(
    fixtureId: string,
    body: {
      rgb?: { r: number; g: number; b: number };
      level?: number;
      on?: boolean;
      subfixture?: number;
      preset?: string;
    },
  ): Promise<unknown> {
    return this.request<unknown>('POST', `/dmx/fixtures/${encodeURIComponent(fixtureId)}/state`, body);
  }

  /** POST /dmx/fixtures/{id}/effect - arm a named effect. An EMPTY `effect`
   *  string means STOP whatever is running, which is OPBridge's convention
   *  and not a quirk of ours: `""` after trim routes to `stop_effect_local`.
   *  `color` tints the effects that take a base hue (strobe / pulse /
   *  bar-pulse) and is ignored by the ones that define their own (fire /
   *  lightning / rainbow / beat-cycle). */
  dmxSetEffect(
    fixtureId: string,
    body: {
      effect: string;
      brightness?: number;
      color?: { r: number; g: number; b: number };
      subfixture?: number;
    },
  ): Promise<unknown> {
    return this.request<unknown>('POST', `/dmx/fixtures/${encodeURIComponent(fixtureId)}/effect`, body);
  }

  /** POST /dmx/blackout - the whole-rig kill. Rig-wide by design: it is the
   *  lighting panic path and takes no target, so it cannot be aimed at the
   *  wrong thing in the one moment you need it. */
  dmxBlackout(): Promise<unknown> {
    return this.request<unknown>('POST', '/dmx/blackout', {});
  }

  /** GET /dmx/fixtures/resolved - per-fixture EFFECTIVE state, reverse
   *  resolved from the live DMX buffer. Carries the colour actually on the
   *  wire plus the hold/park ownership model (`held`, `heldBy`,
   *  `heldCapabilities`, `parked`). Unlike MIDI, this is real closed-loop
   *  feedback: it says what IS, not what we asked for. */
  dmxResolvedState(): Promise<DmxResolvedResponse> {
    return this.request<DmxResolvedResponse>('GET', '/dmx/fixtures/resolved');
  }

  /** GET /dmx/fx-catalog - OPBridge's OWN software effects, split `free` /
   *  `musical`. Distinct from a profile mode's `effects`, which are the
   *  fixture's built-in macros written as channel values. Any surface
   *  offering both must keep the groups apart: only the musical ones follow
   *  the clock. */
  dmxFxCatalog(): Promise<DmxFxCatalogResponse> {
    return this.request<DmxFxCatalogResponse>('GET', '/dmx/fx-catalog');
  }

  /** GET /traffic/midi/recent?direction=in — snapshot of the last (up to
   *  50) inbound MIDI envelopes the daemon has seen, oldest first.
   *  `direction` is required by OPBridge in v1 ("out" is a reserved,
   *  always-empty placeholder) — the PWA only ever asks for `in`. */
  trafficMidiRecent(): Promise<TrafficMidiEntry[]> {
    return this.request<TrafficMidiEntry[]>('GET', '/traffic/midi/recent?direction=in');
  }

  /** GET /traffic/osc/recent?direction=in — same shape, OSC. */
  trafficOscRecent(): Promise<TrafficOscEntry[]> {
    return this.request<TrafficOscEntry[]>('GET', '/traffic/osc/recent?direction=in');
  }

  createFixture(fixture: FixtureInstance): Promise<FixtureInstance> {
    return this.request<FixtureInstance>('POST', '/fixtures', fixture);
  }

  updateFixture(id: string, fixture: FixtureInstance): Promise<FixtureInstance> {
    return this.request<FixtureInstance>('PUT', `/fixtures/${encodeURIComponent(id)}`, fixture);
  }

  deleteFixture(id: string): Promise<void> {
    return this.request<void>('DELETE', `/fixtures/${encodeURIComponent(id)}`);
  }

  /** Send a raw MIDI message to a named output port. `bytes` is the wire
   *  payload (e.g. `[0x90, 60, 127]` for note-on C4 vel 127). Smoke-path
   *  for the new HTTP transport — used by the dev "Send test MIDI" button
   *  in the Bridges panel. Production callers will hit higher-level
   *  helpers once Phase 2 wires them. */
  sendMidi(port: string, bytes: number[]): Promise<{ ok: boolean; bytes_sent: number }> {
    return this.request<{ ok: boolean; bytes_sent: number }>('POST', '/midi/send', { port, bytes });
  }

  /** POST /osc/send — encode + send an OSC message (Tier 1 RT). `args` is a
   *  naked array; OSC types are inferred by OPBridge (a JSON integer → int32,
   *  a decimal → float32, a string → OSC string). Granted by the
   *  `opbuttons-full` bundle. Used by the Transpose item to drive the
   *  OP-AbletonTranspose M4L device. */
  oscSend(
    address: string,
    args: (number | string | boolean)[],
    target: string,
  ): Promise<{ ok?: boolean }> {
    return this.request<{ ok?: boolean }>('POST', '/osc/send', { address, args, target });
  }

  // ─── Engine library push (v1.13.9+) ──────────────────────────────
  // Whole-library POST and per-instance PUT/DELETE. The PUT-per-edit
  // path is the production default for live editing (one HTTP round-trip
  // per sequencer/macro change instead of re-pushing the whole library).
  // POST whole-library stays for bulk import + bulk delete UX flows
  // (per the v1.13.9 OPBridge ship notes).

  /** POST /sequencers/{consumer} — replace the whole library. Use for
   *  bulk import or initial-snapshot push; per-edit changes should go
   *  through {@link putSequencer} instead. */
  postSequencerLibrary(consumer: string, library: unknown): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(
      'POST', `/sequencers/${encodeURIComponent(consumer)}`, library,
    );
  }

  /** PUT /sequencers/{consumer}/{id} — insert-or-update one sequencer.
   *  Returns `{ok, created}` where `created=true` on insert, `false` on
   *  update. The body must carry `id` matching the path (OPBridge 400s
   *  on mismatch). */
  putSequencer(
    consumer: string,
    id: string,
    sequencer: { id: string },
  ): Promise<{ ok: boolean; created: boolean }> {
    return this.request<{ ok: boolean; created: boolean }>(
      'PUT', `/sequencers/${encodeURIComponent(consumer)}/${encodeURIComponent(id)}`, sequencer,
    );
  }

  /** POST /sequencers/{consumer}/arm — toggle-arm one sequencer by id. A
   *  2nd call while armed/running flips it to Killing (the engine drains
   *  hanging note-offs). Body is `{ sequencerId }`. Returns
   *  `{ ok, sequencerId, status }` (status = idle|armed|running|killing|
   *  stopping). 404 if the id isn't in this (consumer, user) library — push
   *  it via {@link putSequencer} first, under the SAME bearer (the engine
   *  keys library + runtimes on the token's user_id). */
  armSequencer(
    consumer: string,
    sequencerId: string,
  ): Promise<{ ok: boolean; sequencerId: string; status: string }> {
    return this.request<{ ok: boolean; sequencerId: string; status: string }>(
      'POST', `/sequencers/${encodeURIComponent(consumer)}/arm`, { sequencerId },
    );
  }

  /** DELETE /sequencers/{consumer}/{id} — remove one sequencer. Returns
   *  `{ok, killed}` where `killed=1` if a runtime was running for that id
   *  (flipped to Killing), else 0. 404 if id absent (no auto-create from
   *  DELETE). */
  deleteSequencer(
    consumer: string,
    id: string,
  ): Promise<{ ok: boolean; killed: number }> {
    return this.request<{ ok: boolean; killed: number }>(
      'DELETE', `/sequencers/${encodeURIComponent(consumer)}/${encodeURIComponent(id)}`,
    );
  }

  /** POST /macros/{consumer} — replace the whole macro library. Same
   *  bulk-only contract as {@link postSequencerLibrary}. */
  postMacroLibrary(consumer: string, library: unknown): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(
      'POST', `/macros/${encodeURIComponent(consumer)}`, library,
    );
  }

  /** PUT /macros/{consumer}/{id} — insert-or-update one macro. */
  putMacro(
    consumer: string,
    id: string,
    macro: { id: string },
  ): Promise<{ ok: boolean; created: boolean }> {
    return this.request<{ ok: boolean; created: boolean }>(
      'PUT', `/macros/${encodeURIComponent(consumer)}/${encodeURIComponent(id)}`, macro,
    );
  }

  /** POST /macros/{consumer}/arm — toggle-arm one macro by id. A 2nd call
   *  while armed/running flips it to Killing (each lane snaps to center).
   *  Body is `{ macroId }`. Returns `{ ok, macroId, status }`. 404 if the id
   *  isn't in this (consumer, user) library — push it via {@link putMacro}
   *  first under the SAME bearer. */
  armMacro(
    consumer: string,
    macroId: string,
  ): Promise<{ ok: boolean; macroId: string; status: string }> {
    return this.request<{ ok: boolean; macroId: string; status: string }>(
      'POST', `/macros/${encodeURIComponent(consumer)}/arm`, { macroId },
    );
  }

  /** DELETE /macros/{consumer}/{id} — remove one macro. Same shape as
   *  {@link deleteSequencer}. */
  deleteMacro(
    consumer: string,
    id: string,
  ): Promise<{ ok: boolean; killed: number }> {
    return this.request<{ ok: boolean; killed: number }>(
      'DELETE', `/macros/${encodeURIComponent(consumer)}/${encodeURIComponent(id)}`,
    );
  }

  // ─── Toggle engine — push library + flip (2026-08-16) ────────────
  // A toggle is a stateful two-way primitive: `onAction`/`offAction` are
  // {@link RuleAction}s (the exact same shape a rule action already uses),
  // `state` lives on OPBridge so it survives the tab closing (the whole
  // point — a pedal-fired flip must be visible on reconnect). Byte-verified
  // live against the real dev-PC daemon 2026-08-16 (PUT + GET round-trip,
  // no flip triggered to avoid a live MIDI send during the probe) — field
  // names below are camelCase exactly as returned, not guessed.

  /** GET /toggles/{consumer} — the whole toggle library WITH current state.
   *  `state` is the queryable status OPBridge's contract promises — no
   *  separate status endpoint. */
  getToggles(consumer: string): Promise<TogglesLibrary> {
    return this.request<TogglesLibrary>('GET', `/toggles/${encodeURIComponent(consumer)}`);
  }

  /** PUT /toggles/{consumer}/{id} — insert-or-update one toggle. Body's
   *  `id` must match the path. Idempotent — safe to push on every tap
   *  before flipping, same as {@link putSequencer}. */
  putToggle(
    consumer: string,
    id: string,
    /** `state` is optional on the wire but NOT optional in effect: OPBridge
     *  defaults a missing one to false, so a re-push of an existing toggle
     *  must carry its current state (see `dispatch.ts` `pushToggleOnBridge`). */
    toggle: { id: string; name?: string; onAction: RuleAction; offAction: RuleAction; state?: boolean },
  ): Promise<{ ok: boolean; created: boolean }> {
    return this.request<{ ok: boolean; created: boolean }>(
      'PUT', `/toggles/${encodeURIComponent(consumer)}/${encodeURIComponent(id)}`, toggle,
    );
  }

  /** PUT /admin/actions/{id} - register (or replace) one named action that
   *  mods can be granted and fire via `POST /actions/{id}/fire`. A pairing
   *  bearer may do this only when its scope covers `/admin/actions/{id}`
   *  (OPBridge DEC-597), and then only for payloads it could send itself and
   *  never over another app's action. `owner` is stamped by OPBridge. Until
   *  OPBridge mounts the route where the PWA reaches it and grants the scope
   *  (held for the operator, 2026-09-23), this answers 401 or 404. */
  /** Replay a request recorded by `store.ts::captureRequests` - the setup a
   *  shared item needs on OPBridge before a mod can press it (a toggle
   *  definition, a sequencer pattern, a timeline). Exactly the request the
   *  item's own code would have made, so the two cannot disagree. */
  replay(req: { method: 'GET' | 'POST' | 'PUT' | 'DELETE'; path: string; body: unknown }): Promise<unknown> {
    return this.request<unknown>(req.method, req.path, req.body);
  }

  putNamedAction(id: string, action: NamedAction): Promise<unknown> {
    return this.request<unknown>('PUT', `/admin/actions/${encodeURIComponent(id)}`, action);
  }

  /** POST /admin/actions - register and withdraw many named actions in ONE
   *  config write (OPBridge S290, live since v1.15.161). All or nothing: one
   *  refused entry refuses the batch and writes nothing (403 `{error, id}`).
   *  At most 512 entries; a delete of a missing id lands in `missing`. */
  batchNamedActions(body: { put?: NamedAction[]; delete?: string[] }): Promise<{
    ok: boolean; put?: string[]; deleted?: string[]; missing?: string[]; owned?: string[];
  }> {
    return this.request('POST', '/admin/actions', body);
  }

  /** GET /admin/controllers - OPBridge's controller registry: units and their
   *  controls (permanent ids, renamable codes), pages, bindings, the active
   *  page. Replicated mesh-wide, so any bridge answers for all. Scope
   *  `/controllers` (`opbuttons-full` v22). DEC-256. */
  getControllers(): Promise<ControllerRegistry> {
    return this.request<ControllerRegistry>('GET', '/admin/controllers');
  }

  /** PUT /admin/controllers/bindings - `(page, control) -> named action`.
   *  No `actionId` removes the binding. Scope `/controllers/bind`. Bind to
   *  the control's `id`, never its code (codes are renamed). DEC-256. */
  putControllerBinding(b: ControllerBindingWrite): Promise<unknown> {
    return this.request<unknown>('PUT', '/admin/controllers/bindings', b);
  }

  /** DELETE /admin/actions/{id} - withdraw a named action; every mod's fire
   *  on it then 404s. Owned actions only, same rule as the PUT. */
  deleteNamedAction(id: string): Promise<unknown> {
    return this.request<unknown>('DELETE', `/admin/actions/${encodeURIComponent(id)}`);
  }

  /** DELETE /toggles/{consumer}/{id} — remove one toggle. */
  deleteToggle(consumer: string, id: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(
      'DELETE', `/toggles/${encodeURIComponent(consumer)}/${encodeURIComponent(id)}`,
    );
  }

  /** POST /toggles/{consumer}/flip — flips the bool, fires whichever of
   *  onAction/offAction now applies, persists, returns the new state
   *  synchronously. 404 if the id isn't in this (consumer, user) library —
   *  push it via {@link putToggle} first, under the SAME bearer. */
  flipToggle(
    consumer: string,
    toggleId: string,
  ): Promise<{ ok: boolean; toggleId: string; state: boolean }> {
    return this.request<{ ok: boolean; toggleId: string; state: boolean }>(
      'POST', `/toggles/${encodeURIComponent(consumer)}/flip`, { toggleId },
    );
  }

  // ─── Consumer document store (rig-truth doc sync) ────────────────
  // PUT /docs/{consumer}/{doc_id} body { base_rev, body } → 200 { rev, hash }
  // with monotonic u64 rev + SHA-256 body hash, or 409 { server_rev } on an
  // optimistic-concurrency conflict (deliberately NOT last-writer-wins). First
  // used by the Waves-autotune config sync (removed 2026-09-28, DEC-102
  // RETIRED); now by project, matrix and rig-state sync.

  /** PUT /docs/{consumer}/{doc_id} — upsert one rev-numbered document. The
   *  `body` is the doc payload; `base_rev` is the last rev this client saw
   *  (0 for a fresh doc). Resolves to the new `{ rev, hash }`. Throws
   *  `OpbridgeError` kind `'http'` httpStatus 409 on a rev conflict (body
   *  carries `server_rev`) — the caller adopts it and retries. */
  docPut(
    consumer: string,
    docId: string,
    payload: { base_rev: number; body: unknown },
  ): Promise<{ rev: number; hash: string }> {
    return this.request<{ rev: number; hash: string }>(
      'PUT', `/docs/${encodeURIComponent(consumer)}/${encodeURIComponent(docId)}`, payload,
    );
  }

  /** GET /docs/{consumer}/{doc_id} — read one document.
   *
   *  Response shape verified against OPBridge's own handler
   *  (`handlers/docs.rs::read_one`), not assumed: 200 `{ rev, hash, body }`,
   *  404 `{ error: "no such doc" }`. Callers treat a 404 as "nothing stored
   *  yet", which is the normal first-run case. */
  docGet<T = unknown>(
    consumer: string,
    docId: string,
  ): Promise<{ rev: number; hash: string; body: T }> {
    return this.request<{ rev: number; hash: string; body: T }>(
      'GET', `/docs/${encodeURIComponent(consumer)}/${encodeURIComponent(docId)}`,
    );
  }

  /** GET /docs/{consumer} — list every document for a consumer with its
   *  current `rev` and body `hash`, WITHOUT fetching any bodies.
   *
   *  Response shape captured off the live daemon 2026-08-31, not assumed:
   *  `200 {"docs":[{"doc_id":"projects","rev":333,"hash":"bbf5..."}]}`.
   *
   *  This is what makes the per-project doc split cheap: one small request
   *  tells us which projects exist on the bridge and which of them have
   *  moved since we last looked, so a sync tick fetches only the bodies
   *  that actually changed instead of the whole library every time. */
  docList(consumer: string): Promise<{ docs: { doc_id: string; rev: number; hash: string }[] }> {
    return this.request<{ docs: { doc_id: string; rev: number; hash: string }[] }>(
      'GET', `/docs/${encodeURIComponent(consumer)}`,
    );
  }

  /** DELETE /docs/{consumer}/{doc_id} — remove one document (e.g. when its
   *  source item is deleted so OPBridge stops advertising it).
   *
   *  ✅ Confirmed working (HTTP 200) against the live daemon 2026-08-31.
   *  An older note in this repo recorded it as a 405 that OPBridge had
   *  never wired; that is out of date and the doc-store cleanup paths
   *  which were disabled on account of it can be re-enabled. */
  docDelete(consumer: string, docId: string): Promise<void> {
    return this.request<void>(
      'DELETE', `/docs/${encodeURIComponent(consumer)}/${encodeURIComponent(docId)}`,
    );
  }

  // ─── Inbound MIDI learn-capture + rules (DEC-154) ────────────────
  // GET /midi/recent drains OPBridge's inbound-MIDI ring (subscribed on
  // every input port, including the dedicated `OPButtons-Control` loopMIDI
  // port DEC-154's Learn flow filters to). Already granted in the
  // `opbuttons-full` bundle — no scope change needed (confirmed against
  // OPBridge S154/S155, `OApps/opbridge-opbuttons.md`).
  //
  // ✅ WIRE SHAPE NOW VERIFIED LIVE (2026-08-27), against a real Midi Fighter
  // Twister on the dev-PC daemon. The 2026-08-13 note here asked for exactly
  // this ("re-probe with a real pedal press BEFORE trusting this at a gig")
  // and it was never done; doing it found TWO real bugs, both of which had
  // shipped:
  //
  //   1. The response is `{"messages":[...]}`, NOT a bare array. This method
  //      declared `MidiRecentEntry[]`, so `pollForMidiTrigger`'s
  //      `entries.length` read `undefined`, `undefined - 1` gave NaN, and the
  //      scan loop never executed once. DEC-154's Learn capture could never
  //      succeed - it span for its full 15s timeout and reported nothing, no
  //      matter how the pedal was stomped. Silent, because indexing an object
  //      as an array throws nothing.
  //   2. `status` arrives already masked with `channel` alongside it, so
  //      `learn.ts` deriving `status & 0x0f` produced channel 0 always. See
  //      `MidiRecentEntry` above.
  //
  // Both are fixed. The lesson is the general one: a wire shape described in
  // prose is a hypothesis, and this one was wrong in two independent ways.

  /** GET /midi/recent — DRAIN the daemon's inbound-MIDI ring, oldest first.
   *
   *  ⚠️ **DESTRUCTIVE.** OPBridge's handler calls `drain_recent()`, which
   *  POPS the ring (opbridge-core `handlers/midi.rs`). Whoever polls first
   *  takes the messages and every other consumer sees nothing. Do not add a
   *  second independent poller of this route; route new consumers through
   *  `consumerWs.ts`'s `onMidiIn()` sink instead. `/traffic/midi/recent` is
   *  the non-destructive read if you only want to observe.
   *
   *  Unwraps the `{ messages }` envelope so callers get the array they
   *  expect. Tolerates a bare array too, so a future daemon that drops the
   *  envelope does not silently return nothing. */
  async midiRecent(): Promise<MidiRecentEntry[]> {
    const resp = await this.request<MidiRecentResponse | MidiRecentEntry[]>('GET', '/midi/recent');
    if (Array.isArray(resp)) return resp;
    return Array.isArray(resp?.messages) ? resp.messages : [];
  }

  /** POST /rules/{consumer} — replace the WHOLE rule set. There is no
   *  per-rule PUT (confirmed against `handlers/rules.rs::push` — unlike
   *  sequencers/macros, rules have no partial-update endpoint), so the
   *  caller must hold the full set locally and push it whole on every
   *  change. See `midiTrigger/ruleSync.ts`. */
  putRuleSet(consumer: string, ruleSet: RuleSet): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(
      'POST', `/rules/${encodeURIComponent(consumer)}`, ruleSet,
    );
  }

  // ─── LFO engine (v1.13 engines) ─────────────────────────────────
  // POST /lfos/{consumer}/start — body is LfoParams:
  //   { id, target: {type:"midi", port, channel, ccNumber},
  //     periodBeats, amplitude, center, waveform, loop }
  // amplitude + center are in the engine's unit range [0..1] (not MIDI
  // 0..127); the caller is responsible for normalising.

  /** POST /lfos/{consumer}/start — arm an LFO. Returns `{ok, lfoId}` on
   *  success. 422 on validation fail; 503 if engines service isn't wired. */
  startLfo(
    consumer: string,
    params: {
      id: string;
      target: { type: 'midi'; port: string; channel: number; ccNumber: number };
      periodBeats: number;
      amplitude: number; // 0..1
      center: number;    // 0..1
      waveform: 'sine' | 'saw';
      loop: boolean;
    },
  ): Promise<{ ok: boolean; lfoId: string }> {
    return this.request<{ ok: boolean; lfoId: string }>(
      'POST', `/lfos/${encodeURIComponent(consumer)}/start`, params,
    );
  }

  /** POST /lfos/{consumer}/stop — stop one LFO. Body carries the lfo id.
   *  Returns `{ok, lfoId}` on success. 404 if the id has no active
   *  runtime for this consumer + user. */
  stopLfo(consumer: string, lfoId: string): Promise<{ ok: boolean; lfoId: string }> {
    return this.request<{ ok: boolean; lfoId: string }>(
      'POST', `/lfos/${encodeURIComponent(consumer)}/stop`, { lfoId },
    );
  }

  // ─── Timeline engine (opbridge-timelines, S218 — 2026-08-16) ────────
  // Byte-verified against OPBridge's own handler source
  // (`crates/opbridge-core/src/handlers/timelines.rs`), not guessed: fire/
  // start take `{id}`, stop/pause/resume take no body (there is at most
  // ONE active Song per (consumer, user) — `timelines.stop`/etc. need no
  // id to disambiguate), jumpToSection takes `{sectionId}`. Milestone
  // scope shipped: M2 (Action + Cue) + core of M3 (Song playhead, section
  // jump, pause/resume) — DMX Animation (M4) / Sequencer+Macro placement
  // (M6) / Marker steps (M7) are NOT understood server-side yet, so a
  // pushed library must only carry `cue` steps (see
  // `timeline/bridgeSync.ts::toBridgeTimeline`).

  /** POST /timelines/{consumer} — replace the WHOLE timeline library for
   *  this (consumer, user). There is no per-tier split on the wire. */
  postTimelineLibrary(consumer: string, library: unknown): Promise<{ ok: boolean; timelines: number }> {
    return this.request<{ ok: boolean; timelines: number }>(
      'POST', `/timelines/${encodeURIComponent(consumer)}`, library,
    );
  }

  /** PUT /timelines/{consumer}/{id} — insert-or-update ONE timeline, others
   *  untouched. Body's `id` must equal the path id (OPBridge 400s on
   *  mismatch). Idempotent — push right before every fire/start, same
   *  "keep live edits in sync" contract as {@link putSequencer}.
   *
   *  `live` (OPBridge S306, `/health.capabilities.timeline_live_update`) adds
   *  `?live=true`: the copy also replaces every RUNNING instance on its next
   *  tick without moving the playhead, and the reply carries `live` = how
   *  many running copies took it (0 = not playing). Only send it to a bridge
   *  that reports the capability. */
  putTimeline(
    consumer: string,
    id: string,
    timeline: { id: string },
    opts: { live?: boolean; leaseHolder?: string | null } = {},
  ): Promise<{ ok: boolean; created: boolean; live?: number; warnings?: Array<{ step?: string; preset?: string; error?: string }> }> {
    return this.request<{ ok: boolean; created: boolean; live?: number; warnings?: Array<{ step?: string; preset?: string; error?: string }> }>(
      'PUT', `/timelines/${encodeURIComponent(consumer)}/${encodeURIComponent(id)}${opts.live ? '?live=true' : ''}`, timeline,
      // S329: the edit lease's holder, so a device that no longer holds it is refused (409 `lease_held`).
      opts.leaseHolder ? { headers: { 'X-OPBridge-Lease-Holder': opts.leaseHolder } } : {},
    );
  }

  /** OPBridge S329: claim, renew, take over or discard a timeline's edit lease (`timeline/lease.ts`). */
  leaseTimeline(consumer: string, id: string, body: { holder: string; holderName?: string; mode?: 'open' | 'renew' | 'takeover' | 'discard' }): Promise<{ held: boolean; holder: { holder: string; holderName: string; app: string; since: number } | null; lost: { mode: string; by: string; byName: string; app: string } | null; renewSecs: number; freshSecs: number }> {
    return this.request('POST', `/timelines/${encodeURIComponent(consumer)}/${encodeURIComponent(id)}/lease`, body);
  }
  /** OPBridge S329: who holds a timeline's edit lease now (null for nobody). */
  leaseState(consumer: string, id: string): Promise<{ holder: { holder: string; holderName: string } | null; claims: unknown[] }> {
    return this.request('GET', `/timelines/${encodeURIComponent(consumer)}/${encodeURIComponent(id)}/lease`);
  }
  /** OPBridge S329: let go of a timeline's edit lease (the editor closed). */
  releaseLease(consumer: string, id: string, holder: string): Promise<unknown> {
    return this.request('DELETE', `/timelines/${encodeURIComponent(consumer)}/${encodeURIComponent(id)}/lease`, { holder });
  }

  /** DELETE /timelines/{consumer}/{id} — remove one timeline; kills its
   *  running instances. Returns `{ok, stopped}`. */
  deleteTimeline(consumer: string, id: string): Promise<{ ok: boolean; stopped: boolean }> {
    return this.request<{ ok: boolean; stopped: boolean }>(
      'DELETE', `/timelines/${encodeURIComponent(consumer)}/${encodeURIComponent(id)}`,
    );
  }

  /** POST /timelines/{consumer}/fire — start a fresh Action-tier instance
   *  (fire-and-forget; multiple concurrent instances of the same Action
   *  timeline are legal). Returns `{ok, instanceId}`. 422 if the id isn't
   *  in the library — push it via {@link putTimeline} first. */
  fireTimeline(consumer: string, id: string, quantize?: TimelineQuantize): Promise<{ ok: boolean; instanceId: string }> {
    return this.request<{ ok: boolean; instanceId: string }>(
      'POST', `/timelines/${encodeURIComponent(consumer)}/fire`, quantize ? { id, quantize } : { id },
    );
  }

  /** POST /timelines/{consumer}/start — (re)start the Song-tier timeline
   *  from bar 0. At most one Song is active per (consumer, user); starting
   *  a different one replaces it. 422 if the id isn't in the library.
   *  `quantize` (both routes, OPBridge S306, `/health.capabilities.
   *  timeline_quantize`): `instant` or `nextBar`; left out, `start` waits for
   *  the bar and `fire` does not. Any other value is a 400, and an older
   *  bridge may refuse the field, so only send it where the capability says.
   *  `from: "editor"` (OPBridge S351, `editor_play_takes_lost_clock`): the timeline editor's Play, and only that; with
   *  the clock's leader not heard, this PC takes the clock instead of a 409. An older bridge ignores it. */
  startTimeline(consumer: string, id: string, quantize?: TimelineQuantize, fromBar?: number, from?: 'editor'): Promise<TimelineStartAnswer> {
    return this.request<TimelineStartAnswer>(
      'POST', `/timelines/${encodeURIComponent(consumer)}/start`,
      { id, ...(quantize ? { quantize } : {}), ...(fromBar !== undefined ? { fromBar } : {}), ...(from ? { from } : {}) },
    );
  }

  /** POST /timelines/{consumer}/stop — stop the active Song timeline. No
   *  body — operates on whichever Song is currently active. `stopped` is
   *  false when nothing was running. */
  stopTimeline(consumer: string): Promise<{ ok: boolean; stopped: boolean }> {
    return this.request<{ ok: boolean; stopped: boolean }>(
      'POST', `/timelines/${encodeURIComponent(consumer)}/stop`,
    );
  }

  /** POST /timelines/{consumer}/pause — pause the active Song timeline
   *  (playhead freezes; resume continues from the same bar). No body. */
  pauseTimeline(consumer: string): Promise<{ ok: boolean; paused: boolean }> {
    return this.request<{ ok: boolean; paused: boolean }>(
      'POST', `/timelines/${encodeURIComponent(consumer)}/pause`,
    );
  }

  /** POST /timelines/{consumer}/resume. No body. */
  resumeTimeline(consumer: string): Promise<{ ok: boolean; resumed: boolean }> {
    return this.request<{ ok: boolean; resumed: boolean }>(
      'POST', `/timelines/${encodeURIComponent(consumer)}/resume`,
    );
  }

  /** POST /timelines/{consumer}/jumpToSection — quantized to next bar.
   *  Only meaningful against an already-active Song (422 otherwise — the
   *  caller should `startTimeline` first when nothing is active). */
  /** POST /timelines/{consumer}/jumpToBar - a playing song timeline moves to timeline bar `bar` (0-based) on the next
   *  bar line, its song with it. Asked of OPBridge 2026-10-13 (`timeline_start_at_bar`); only sent where advertised. */
  jumpToBarTimeline(consumer: string, bar: number, quantize?: 'instant'): Promise<JumpToBarAnswer> {
    return this.request<JumpToBarAnswer>('POST', `/timelines/${encodeURIComponent(consumer)}/jumpToBar`, quantize ? { bar, quantize } : { bar });
  }

  /** POST /timelines/{consumer}/loopRange (OPBridge S324, `timeline_loop_range`): loop 0-based bars [fromBar, toBar)
   *  of the running song on OPBridge, or `null` to clear it. Always over fetch: OPBridge's WebTransport bridge turns a
   *  JSON `null` body into no body (`opbridge-webtransport` `body: Option<Value>`), so a clear would fail there and the
   *  song would keep looping. When fetch cannot reach the bridge the set fails too, and the editor loops by jumping. */
  /** POST /timelines/{consumer}/touch (OPBridge S324, `timeline_touch_override`): a hand let go of (port, channel
   *  0-based, cc), so a run holding that CC back sends it again per its `touchMode`. 404 `not_touched` when nothing
   *  was held. */
  touchTimeline(consumer: string, body: { port: string; channel: number; cc: number; state: 'release' | 'touch'; value?: number }): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>('POST', `/timelines/${encodeURIComponent(consumer)}/touch`, body);
  }

  loopRangeTimeline(consumer: string, range: { fromBar: number; toBar: number } | null): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>('POST', `/timelines/${encodeURIComponent(consumer)}/loopRange`, range, { fetchOnly: true });
  }

  /** With `id` (OPBridge S339, `timeline_jump_starts`, released v1.15.222): ONE call that jumps when `id` is the active
   *  Song and STARTS `id` at that section on the next bar line when it is not (answers `started: true`). Without `id`,
   *  the plain jump (422 when nothing runs). */
  jumpToSectionTimeline(consumer: string, sectionId: string, id?: string): Promise<{ ok: boolean; started?: boolean }> {
    return this.request<{ ok: boolean; started?: boolean }>(
      'POST', `/timelines/${encodeURIComponent(consumer)}/jumpToSection`, id ? { sectionId, id } : { sectionId },
    );
  }

  /** GET /timelines/{consumer}/state — Song playhead + which Action
   *  instances are currently running. Action-tier has no per-instance
   *  position, only presence in `activeActionIds`. */
  getTimelineState(consumer: string): Promise<TimelineStateSnapshot> {
    return this.request<TimelineStateSnapshot>('GET', `/timelines/${encodeURIComponent(consumer)}/state`);
  }

  /** GET /timelines/{consumer} - every timeline OPBridge holds for this consumer, as pushed (fields it does not model,
   *  `modified` among them, kept verbatim: opbridge-timelines `Extra`). */
  getTimelineLibrary(consumer: string): Promise<{ timelines?: Array<{ id: string; name?: string; modified?: string }> }> {
    return this.request<{ timelines?: Array<{ id: string; name?: string; modified?: string }> }>('GET', `/timelines/${encodeURIComponent(consumer)}`);
  }

  // ─── Engines state (unified snapshot) ──────────────────────────────
  // GET /engines/{consumer}/state returns the LFO + Sequencer + Macro
  // runtimes for this (consumer, user_id). Sprint Session 3's slice
  // polls this at 1Hz to translate the LFO array to legacy `lfo.state`
  // event-bus messages so the existing SineLfo card reacts to real
  // engine state transitions (armed → running → stopping → idle)
  // instead of only its own optimistic flip.

  /** POST /midi/panic — OPBridge's own GLOBAL all-notes-off, across every
   *  output port it owns (not just our `defaultMidiOutput`). Bearer-authed
   *  via the `/midi/panic` scope, granted by `opbuttons-full` bundle v2 —
   *  which auto-refreshes onto an already-issued bearer, so no re-pair.
   *
   *  This is strictly wider than the PWA-side v0 CC storm: v0 can only
   *  reach the one port it knows about, this reaches all of them. Shape
   *  verified live 2026-07-28: `{"errors":[],"ok":true,"ports_hit":4}`. */
  midiPanic(): Promise<MidiPanicResponse> {
    return this.request<MidiPanicResponse>('POST', '/midi/panic');
  }

  /** POST /performance-mode - switch the ONE suite-wide performance mode
   *  (OPBridge PROTOCOL.md "Performance mode - ONE suite-wide switch").
   *  Data plane, scope `/performance-mode`, carried by `opbuttons-full` since
   *  bundle v13. One bridge is enough: the mesh carries it to the others.
   *  Answers `{ enabled, restart_required: false }`; setting the value it
   *  already has is harmless. A 403 means this device is on an old
   *  hand-scoped token without the scope, and re-pairing fixes it. */
  setPerformanceMode(enabled: boolean): Promise<{ enabled: boolean; restart_required?: boolean }> {
    return this.request('POST', '/performance-mode', { enabled });
  }

  /** GET /lights/default - the timeline performance mode plays underneath every other as the DEFAULT LIGHTS (OPBridge
   *  DEC-734; the route agreed 2026-10-27, OPBridge S340, `OPApps/opbridge-opbuttons.md`). One setting for every PC.
   *  Asked for `{ timelineId: string | null }`. Only where `/health.capabilities.default_lights_route` (the name we
   *  proposed; not built when this was written). */
  lightsDefault(): Promise<{ timelineId: string | null }> {
    return this.request('GET', '/lights/default');
  }

  /** POST /lights/default - make `timelineId` the default lights (the same setting OPBridge's dashboard sets). */
  setLightsDefault(timelineId: string): Promise<{ timelineId: string | null }> {
    return this.request('POST', '/lights/default', { timelineId });
  }

  /** POST /engines/{consumer}/stopAll — aggregate kill for every engine
   *  family at once (`lfo.stop_all` + `sequencer.kill_all` +
   *  `macro_.kill_all`). Covered by the existing `/engines/opbuttons/**`
   *  scope, so this needed no bundle change.
   *
   *  Wider than the v0 path in the way that matters: v0 enumerates LFOs
   *  and stops them one by one, so it cannot stop a sequencer or macro at
   *  all, and it stops nothing if the enumerate call fails. Note OPBridge
   *  documents stopAll as GRACEFUL (engines drain note-offs through the
   *  tick loop), which is why it pairs with — rather than replaces — the
   *  transport-independent panic. Shape verified live 2026-07-28:
   *  `{"ok":true,"stopped":{"lfos":0,"macros":0,"sequencers":0},"total":0}`. */
  stopAllEnginesNative(consumer: string): Promise<StopAllEnginesResponse> {
    return this.request<StopAllEnginesResponse>(
      'POST', `/engines/${encodeURIComponent(consumer)}/stopAll`,
    );
  }

  /** GET /engines/{consumer}/state — snapshot of every active engine
   *  runtime. Returns the OPBridge `EnginesStateSnapshot` envelope. */
  enginesState(consumer: string): Promise<EnginesStateSnapshotResponse> {
    return this.request<EnginesStateSnapshotResponse>(
      'GET', `/engines/${encodeURIComponent(consumer)}/state`,
    );
  }

  /** GET /clock/status — OPBridge's musical-time snapshot. Bearer-authed
   *  (needs `/clock/status` scope, granted by `opbuttons-full`). This is
   *  the clock source for the transport readout + smoothClock anchor
   *  post-migration. 503 if the Ableton clock isn't wired (test / partial
   *  OPBridge setup) — caller treats that as "no clock" (stub mode). */
  clockStatus(): Promise<ClockStatus> {
    return this.request<ClockStatus>('GET', '/clock/status');
  }

  /** GET /mixer/status — Live's per-track mixer state from the OP-AbletonMixer
   *  feed (DEC-467). Bearer-authed (`opbuttons-full` covers /mixer/status).
   *  Drives the Mixer strips' feedback half via mixer/statusPoll.ts. See
   *  {@link MixerStatus} for the shape + the on-wire gotchas. */
  mixerStatus(): Promise<MixerStatus> {
    return this.request<MixerStatus>('GET', '/mixer/status');
  }

  // ─── Test tracks (2026-10-13; plan .notepads/test-track-timeline.md) ──
  // OPBridge holds a song file, plays it on a PC and output the operator picks,
  // and keeps its beat map and section marks. Shapes are the ones OPButtons
  // posted to OPApps/opbridge-opbuttons.md (2026-10-13) for OPBridge to build
  // to; a bridge advertises them with `capabilities.test_tracks`. Until then
  // every call 404s, which callers show as "this OPBridge has no test tracks".

  /** GET /test-tracks - every track on any PC (file sync carries them). */
  testTracks(): Promise<{ tracks: TestTrack[] }> {
    return this.request('GET', '/test-tracks');
  }

  /** POST /test-tracks/import - an audio file, multipart field `file` (OPBridge S318: no size limit, 201
   *  `{ ok, track }`, 422 `not_audio`). Always over fetch: a multipart body is not what the JSON request path or
   *  WebTransport carries. OPBridge's helper analyses it after; `GET /test-tracks` shows `analysing` until done.
   *  The same audio again (OPBridge S327, matched byte for byte by its sha256): 200 `{ ok, track, existing: true }`
   *  with THAT track, its marks and tempo pick untouched, and nothing copied or analysed. */
  async importTestTrack(file: File, timeoutMs = 120000, onProgress?: (sent: number, total: number) => void): Promise<{ ok: boolean; track: TestTrack; existing?: boolean }> {
    return this.uploadAudio('/test-tracks/import', file, timeoutMs, onProgress);
  }

  /** POST /test-tracks/{id}/replace - new audio under the same id, re-analysed; marks kept unless `keep` is false
   *  (asked of OPBridge 2026-10-13, `audio_library`). */
  async replaceTestTrack(id: string, file: File, keep: boolean, timeoutMs = 120000, onProgress?: (sent: number, total: number) => void): Promise<{ ok: boolean; track: TestTrack }> {
    return this.uploadAudio(`/test-tracks/${encodeURIComponent(id)}/replace${keep ? '' : '?keep=false'}`, file, timeoutMs, onProgress);
  }

  /** PATCH /test-tracks/{id} - the display name and the type (`audio_library`). */
  patchTestTrack(id: string, patch: { name?: string; kind?: AudioKind }): Promise<{ ok: boolean; track?: TestTrack }> {
    return this.request('PATCH', `/test-tracks/${encodeURIComponent(id)}`, patch, { timeoutMs: 15000 });
  }

  /** DELETE /test-tracks/{id} - the file and its beat map, on every PC (file sync). */
  deleteTestTrack(id: string): Promise<{ ok: boolean }> {
    return this.request('DELETE', `/test-tracks/${encodeURIComponent(id)}`, undefined, { timeoutMs: 15000 });
  }

  /** One audio upload (multipart `file`). XHR, not fetch: only XHR reports upload progress, which the import window
   *  shows (2026-10-13). */
  private uploadAudio(path: string, file: File, timeoutMs: number, onProgress?: (sent: number, total: number) => void): Promise<{ ok: boolean; track: TestTrack; existing?: boolean }> {
    const body = new FormData();
    body.append('file', file, file.name);
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${this.baseUrl}${path}`);
      xhr.timeout = timeoutMs;
      xhr.withCredentials = false;
      if (this.bearer) xhr.setRequestHeader('Authorization', `Bearer ${this.bearer}`);
      if (onProgress) xhr.upload.onprogress = (e) => onProgress(e.loaded, e.lengthComputable ? e.total : file.size);
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText) as { ok: boolean; track: TestTrack }); }
          catch { reject(new OpbridgeError('http', `POST ${path}: the reply was not JSON`, xhr.status)); }
          return;
        }
        const detail = xhr.responseText ?? '';
        reject(new OpbridgeError(xhr.status === 401 ? 'unauthorized' : 'http', `POST ${path} → ${xhr.status}${detail ? `: ${detail}` : ''}`, xhr.status));
      };
      xhr.onerror = () => reject(new OpbridgeError('network', `POST ${path}: OPBridge could not be reached`));
      xhr.ontimeout = () => reject(new OpbridgeError('timeout', `POST ${path}: no answer within ${Math.round(timeoutMs / 1000)} s`));
      xhr.send(body);
    });
  }

  /** GET /test-tracks/{id}/peaks - min/max per `ms` window, mono, -1..1. */
  testTrackPeaks(id: string, ms = 20): Promise<TestTrackPeaks> {
    return this.request('GET', `/test-tracks/${encodeURIComponent(id)}/peaks?ms=${ms}`, undefined, { timeoutMs: 15000 });
  }

  /** GET /test-tracks/{id}/beatmap - the analysis plus the operator's marks. 404 while not analysed. */
  testTrackBeatmap(id: string): Promise<TestTrackBeatmap> {
    return this.request('GET', `/test-tracks/${encodeURIComponent(id)}/beatmap`, undefined, { timeoutMs: 15000 });
  }

  /** PATCH /test-tracks/{id}/beatmap - corrections and section marks; `sections` replaces the list. */
  patchTestTrackBeatmap(id: string, patch: TestTrackBeatmapPatch): Promise<TestTrackBeatmap> {
    return this.request('PATCH', `/test-tracks/${encodeURIComponent(id)}/beatmap`, patch, { timeoutMs: 15000 });
  }

  /** POST /test-tracks/play - on the PC whose node id is `pc`, out of `output`. */
  testTrackPlay(req: TestTrackPlay): Promise<{ ok: boolean }> {
    return this.request('POST', '/test-tracks/play', req);
  }

  /** POST /test-tracks/stop */
  testTrackStop(): Promise<{ ok: boolean }> {
    return this.request('POST', '/test-tracks/stop', {});
  }

  /** POST /test-tracks/jump - bar is 1-based on the chosen tempo. */
  testTrackJump(bar: number): Promise<{ ok: boolean }> {
    return this.request('POST', '/test-tracks/jump', { bar });
  }

  /** GET /test-tracks/status - what plays, where, and the playhead. */
  testTrackStatus(): Promise<TestTrackStatus> {
    return this.request('GET', '/test-tracks/status');
  }

  /** GET /audio/outputs?pc= - that PC's audio outputs by the names it gives them. */
  audioOutputs(pc: string): Promise<{ outputs: AudioOutput[] }> {
    return this.request('GET', `/audio/outputs?pc=${encodeURIComponent(pc)}`);
  }

  /** POST /audio/outputs/latency - set how late an output really is (ms). */
  setOutputLatency(output: string, ms: number, pc?: string): Promise<{ ok: boolean }> {
    return this.request('POST', '/audio/outputs/latency', { output, ms, ...(pc ? { pc } : {}) });
  }

  /** POST /audio/outputs/measure - measure an output's latency by loopback (Windows; 409 on the Mac). */
  measureOutputLatency(output: string, pc?: string): Promise<{ ok?: boolean; latencyMs: number; clicks?: number[] }> {
    return this.request('POST', '/audio/outputs/measure', { output, ...(pc ? { pc } : {}) }, { timeoutMs: 20000 });
  }

  /** POST /pads/play - play a pad (audio_pads). */
  padsPlay(req: PadPlay): Promise<PadPlayAnswer> {
    return this.request('POST', '/pads/play', req, { timeoutMs: 10000 });
  }

  /** POST /pads/release - end a gate pad. */
  padsRelease(padId: string, pc?: string): Promise<{ ok: boolean }> {
    return this.request('POST', '/pads/release', { padId, ...(pc ? { pc } : {}) });
  }

  /** POST /pads/stop - stop one pad, or every pad on every PC. */
  padsStop(target: { padId: string; pc?: string } | { all: true }): Promise<{ ok: boolean }> {
    return this.request('POST', '/pads/stop', target);
  }

  /** POST /pads/preload - decode ahead and open the output, so the first press is quick. */
  padsPreload(trackIds: string[], output?: string, pc?: string): Promise<{ ok: boolean }> {
    return this.request('POST', '/pads/preload', { trackIds, ...(output !== undefined ? { output } : {}), ...(pc ? { pc } : {}) }, { timeoutMs: 20000 });
  }

  /** GET /pads/status?pc= - what is sounding (for the pads' progress rings). */
  padsStatus(pc?: string): Promise<PadsStatus> {
    return this.request('GET', `/pads/status${pc ? `?pc=${encodeURIComponent(pc)}` : ''}`);
  }

  /** GET /pads/settings?pc= - that PC's level ceiling and mute. */
  padsSettings(pc?: string): Promise<PadsSettings> {
    return this.request('GET', `/pads/settings${pc ? `?pc=${encodeURIComponent(pc)}` : ''}`);
  }

  /** POST /pads/settings - set that PC's level ceiling (0..1) and / or mute. */
  setPadsSettings(next: PadsSettings & { pc?: string }): Promise<PadsSettings> {
    return this.request('POST', '/pads/settings', next);
  }

  // ─── Stream Deck via Elgato MCP Deck ────────────────────────────────
  // OPBridge proxies the local `@elgato/mcp-server` (HTTP :9090 → named
  // pipe → Stream Deck app). It MUST be proxied: the MCP server 500s on
  // any request carrying an `Origin` header, so the iPad browser cannot
  // reach it directly even though it binds 0.0.0.0. Only actions the
  // operator put in the Stream Deck "MCP Actions" profile are exposed.

  /** GET /streamdeck/actions — the operator's executable Stream Deck
   *  actions. Feeds the Inspector picker. Expect 503 when the MCP server
   *  isn't running; callers surface that as "unavailable", never as an
   *  empty-but-fine list.
   *
   *  Explicit timeout, NOT the connection's blanket default: OPBridge spawns
   *  `@elgato/mcp-server` lazily on first request and this handler blocks on
   *  that readiness before answering — measured 5.9s cold (OPBridge S165).
   *  The blanket default is `HEALTH_TIMEOUT_MS` (1200ms, connection.ts) —
   *  fine for a health poll, hopelessly short here. This is the second time
   *  that mismatch has caused a "fixed it" regression (streamDeckActions.ts's
   *  retry-after-timeout band-aid, CP-078, papered over the symptom without
   *  ever giving this call its own budget) — fix it at the timeout, not with
   *  another retry. */
  streamDeckActions(): Promise<StreamDeckActionsResponse> {
    return this.request<StreamDeckActionsResponse>('GET', '/streamdeck/actions', undefined, {
      timeoutMs: 10000,
    });
  }

  /** POST /streamdeck/execute — fire one action by its MCP instance UUID.
   *  This genuinely presses the key: whatever sits on it (OBS scene, Hue,
   *  hotkey) executes.
   *
   *  Same cold-start path as `streamDeckActions()` (OPBridge's `execute`
   *  handler also blocks on MCP-server readiness first) — same explicit
   *  timeout, for the same reason. */
  executeStreamDeckAction(actionId: string): Promise<StreamDeckExecuteResponse> {
    return this.request<StreamDeckExecuteResponse>('POST', '/streamdeck/execute', {
      id: actionId,
    }, { timeoutMs: 10000 });
  }

  // ─── OBS Studio scenes ──────────────────
  // Both routes are dual-mounted on the data plane (OPBridge router.rs,
  // S208) so a LAN-origin PWA can reach them, and both are scope-gated:
  // `/obs/scenes` and the LITERAL `/obs/scene/<name>` (a per-scene grant, by
  // OPBridge's design, so one backstage scene can be excluded from a mod
  // token). `opbuttons-full` v14 grants NEITHER - measured 2026-09-19
  // against the running daemon: 401 "missing ... /obs/scenes scope", with
  // `/clock/status` answering 200 on the same bearer as the control. Until
  // that bundle gains them every call here 401s, which is why `obs/scenes.ts`
  // renders `unauthorized` as its own named dead state rather than folding it
  // into a generic failure.

  /** GET /obs/scenes - the scene list + the live one. Cheap on OPBridge's
   *  side but NOT free: it opens a fresh obs-websocket connection per call
   *  by design, so this is polled slowly and only on demand
   *  (`obs/scenePoll.ts`), never at a UI cadence. */
  obsScenes(): Promise<ObsScenesResponse> {
    return this.request<ObsScenesResponse>('GET', '/obs/scenes');
  }

  /** POST /obs/scene/{name} - switch the active program scene.
   *
   *  The name is percent-encoded HERE, once, and nowhere else. OBS scene
   *  names are free text: spaces, `#`, `?` and non-ASCII are all ordinary,
   *  and any of them would otherwise break the path or silently truncate it
   *  at a `?`. A name containing `/` still cannot work - axum's `{name}` is a
   *  single path segment - but encoding turns that from a wrong-route 404
   *  into a clean 422 from OBS itself, which is legible. Flagged to OPBridge
   *  rather than worked around. */
  obsSetScene(scene: string): Promise<ObsSetSceneResponse> {
    return this.request<ObsSetSceneResponse>(
      'POST', `/obs/scene/${encodeURIComponent(scene)}`, {},
    );
  }

  // ─── Program Launch (OPBridge DEC-524) ─────────────────────────────
  // Both routes are dual-mounted on the data plane. Scope-gated on
  // `/programs` and the LITERAL `/program/launch/<id>`, both granted by
  // `opbuttons-full` from v21 (OPBridge v1.15.159, 2026-09-29; measured
  // 401 -> 200 on the same bearer). A bridge older than that 401s, which
  // `program/programs.ts` renders as its own `unauthorized` state.

  /** GET /programs - the programs registered on this machine, with whether
   *  each is running. Cheap: OPBridge answers from its config snapshot and
   *  an in-memory running set, no process table walk. */
  programs(): Promise<ProgramsResponse> {
    return this.request<ProgramsResponse>('GET', '/programs');
  }

  /** POST /program/launch/{id}. The id is percent-encoded HERE, once, and
   *  nowhere else - same rule as `obsSetScene`. A 409 means it is already
   *  running (OPBridge runs one instance per registration). */
  programLaunch(id: string): Promise<ProgramLaunchResponse> {
    return this.request<ProgramLaunchResponse>(
      'POST', `/program/launch/${encodeURIComponent(id)}`, {},
    );
  }

  /** GET /program/launch/{id}/request/{requestId}?timeout= - long-poll one
   *  pending approve-mode launch. Answers as soon as the status changes, or
   *  `pending` at the timeout (OPBridge clamps it to 10s). 404 once the
   *  request is gone. Same scope as the launch itself. */
  programLaunchRequest(
    id: string, requestId: string, timeoutMs: number,
  ): Promise<{ status: 'pending' | 'approved' | 'denied' | 'expired' }> {
    return this.request(
      'GET',
      `/program/launch/${encodeURIComponent(id)}/request/${encodeURIComponent(requestId)}?timeout=${timeoutMs}`,
      undefined,
      { timeoutMs: timeoutMs + 5000 },
    );
  }

  // ─── Lighting presets (OPBridge S287, DEC-601) ─────────────────────
  // Three separate scopes, all in `opbuttons-full` from v19 (OPBridge
  // v1.15.154) with no re-pair. A bridge older than that 404s the routes.

  /** GET /hue/scenes - the Hue app's own scenes on THIS PC's Hue bridges (OPBridge S331, scope `/hue/scenes`,
   *  `opbuttons-full` v26), only in rooms and zones whose every light the operator ticked on its Lights page. A PC with
   *  no Hue bridge answers `{bridges: []}`; there is no `?on=` here, so the app asks each paired PC. */
  hueScenes(): Promise<HueScenesResponse> {
    return this.request<HueScenesResponse>('GET', '/hue/scenes', undefined, { timeoutMs: 10000 });
  }

  // ─── Synesthesia (OPBridge S344/S352, their DEC-756) ──────────────
  // Scopes `/synesthesia/scenes` (list + pictures) and `/synesthesia/control` (fire), both in `opbuttons-full`.
  // Whichever PC is asked, OPBridge answers from (or sends to) the PC that drives Synesthesia over the peer link.

  /** GET /synesthesia/scenes - the scenes a grid shows, the meta controls and commands that PC's OPBridge takes. */
  synesthesiaScenes(): Promise<SynesthesiaScenesResponse> {
    return this.request<SynesthesiaScenesResponse>('GET', '/synesthesia/scenes', undefined, { timeoutMs: 10000 });
  }

  /** GET /synesthesia/picture?name= - one scene's PNG (404 = none). Fetch with the bearer header, like an OPVisual
   *  thumb: a bare `<img src>` would 401, and WebTransport carries text bodies only. */
  async synesthesiaPicture(name: string, timeoutMs = 8000): Promise<Blob> {
    if (!this.bearer) throw new OpbridgeError('unpaired', 'no bearer token configured');
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    const path = `/synesthesia/picture?name=${encodeURIComponent(name)}`;
    try {
      const res = await fetch(`${this.baseUrl}${path}`, { headers: { Authorization: `Bearer ${this.bearer}` }, signal: ctrl.signal, credentials: 'omit' });
      if (!res.ok) throw new OpbridgeError(res.status === 401 ? 'unauthorized' : 'http', `GET ${path} → ${res.status}`, res.status);
      return await res.blob();
    } catch (err) {
      if (err instanceof OpbridgeError) throw err;
      throw new OpbridgeError('network', err instanceof Error ? err.message : String(err));
    } finally {
      clearTimeout(timer);
    }
  }

  /** POST /synesthesia/fire `{command, args}`. OPBridge answers AFTER the wait `args.when` asks for (`bar`, the press
   *  default, is up to a bar), so the timeout covers a slow bar. 404 `not_set_up`, 409 `not_here`, 400 `synesthesia`
   *  (OPBridge's own sentence, e.g. a scene with no mapping) or `unknown_command`. */
  synesthesiaFire(command: string, args: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request<Record<string, unknown>>('POST', '/synesthesia/fire', { command, args }, { timeoutMs: 15000 });
  }

  /** POST /actions/{id}/fire - press a named action (OPBridge S290; scope `/actions/{id}/fire`, `opbuttons-full`
   *  holds every action's fire scope). 404 = no action of that id; 422 = the send itself failed. */
  fireNamedAction(id: string): Promise<{ ok?: boolean; error?: string }> {
    return this.request('POST', `/actions/${encodeURIComponent(id)}/fire`, {});
  }

  /** GET /lighting/presets - the built-in preset catalogue. Read the list
   *  rather than caching ids: a preset can be added on any update. */
  lightingPresets(): Promise<LightingPresetsResponse> {
    return this.request<LightingPresetsResponse>('GET', '/lighting/presets');
  }

  /** POST /lighting/fire - play a preset as a layer. Re-firing the same `id`
   *  replaces that layer in place. 400 names a bad preset, knob, fixture or
   *  field; 503 means this bridge has no DMX driver at all. */
  lightingFire(body: LightingFireBody): Promise<LightingFireResponse> {
    return this.request<LightingFireResponse>('POST', '/lighting/fire', body);
  }

  /** POST /lighting/stop - `{id}` stops that layer (404 if we have none by
   *  that id); no id stops every layer THIS APP started and nothing else. */
  lightingStop(id?: string): Promise<{ ok: boolean; stopped: number }> {
    return this.request<{ ok: boolean; stopped: number }>('POST', '/lighting/stop', id ? { id } : {});
  }

  // ─── OPVisual (DEC-215) ─────────────────────────────────────────────

  /** POST /opvisual/trigger - one OPVisual verb, `{verb, ...fields}`, flat.
   *  OPBridge keeps no verb list: it forwards the body to OPVisual's
   *  `/ws/consumer` socket as `{"event":"opvisual_trigger", ...}` and
   *  OPVisual validates it. Scope `/opvisual/trigger`, in `opbuttons-full`
   *  from v18 with no re-pair. The frames are built in `opvisual/wire.ts`,
   *  and only there. Read {@link OpvisualTriggerResponse} before trusting
   *  a 200. */
  opvisualTrigger(frame: { verb: string } & Record<string, number | string | boolean>): Promise<OpvisualTriggerResponse> {
    return this.request<OpvisualTriggerResponse>('POST', '/opvisual/trigger', frame);
  }

  /** GET /opvisual/state - OPVisual's last frames as OPBridge holds them
   *  (scope `/opvisual/state`). `grid` survives OPVisual closing, so a
   *  listing can be shown while `connected` is false. */
  opvisualState(): Promise<OpvisualStateResponse> {
    return this.request<OpvisualStateResponse>('GET', '/opvisual/state');
  }

  /** One cell picture: `GET /opvisual` + the cell's `thumb`, used EXACTLY as
   *  sent (OPVisual CONTRACT 4c: ids are whole file paths, already encoded;
   *  re-encoding them earns a 400). Fetch only, never WebTransport: that
   *  channel carries text bodies and this is a JPEG. A bare `<img src>` would
   *  401, since OPBridge reads the bearer from the header only. */
  async opvisualThumb(thumb: string, timeoutMs = 8000): Promise<Blob> {
    if (!this.bearer) throw new OpbridgeError('unpaired', 'no bearer token configured');
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const res = await fetch(`${this.baseUrl}/opvisual${thumb}`, {
        headers: { Authorization: `Bearer ${this.bearer}` },
        signal: ctrl.signal,
        credentials: 'omit',
      });
      if (!res.ok) throw new OpbridgeError(res.status === 401 ? 'unauthorized' : 'http', `GET /opvisual${thumb} → ${res.status}`, res.status);
      return await res.blob();
    } catch (err) {
      if (err instanceof OpbridgeError) throw err;
      throw new OpbridgeError('network', err instanceof Error ? err.message : String(err));
    } finally {
      clearTimeout(timer);
    }
  }

  /** OPVisual's program picture: `GET /opvisual/preview.jpg` (OPBridge S314,
   *  scope `/opvisual/state`), one JPEG passed through from OPVisual on the
   *  bridge's own machine. 404 = a bridge that predates the route, 502 =
   *  OPVisual not answering. Fetch with the bearer header, like a thumb. */
  async opvisualPreview(timeoutMs = 4000): Promise<Blob> {
    if (!this.bearer) throw new OpbridgeError('unpaired', 'no bearer token configured');
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const res = await fetch(`${this.baseUrl}/opvisual/preview.jpg`, {
        headers: { Authorization: `Bearer ${this.bearer}` },
        signal: ctrl.signal,
        credentials: 'omit',
        cache: 'no-store',
      });
      if (!res.ok) throw new OpbridgeError(res.status === 401 ? 'unauthorized' : 'http', `GET /opvisual/preview.jpg → ${res.status}`, res.status);
      return await res.blob();
    } catch (err) {
      if (err instanceof OpbridgeError) throw err;
      throw new OpbridgeError('network', err instanceof Error ? err.message : String(err));
    } finally {
      clearTimeout(timer);
    }
  }

  // ─── Ableton device on/off readback ─────────────────────────────────

  /** GET /devices/status - what `OP-ButtonsDevice` has reported about named
   *  devices on named tracks.
   *
   *  Cheap, unlike `obsScenes()`: OPBridge serves it from an in-memory store
   *  written only when a UDP packet arrives, with no socket, task or timer
   *  behind it. The poll is still demand-gated (`ableton/devicePoll.ts`) -
   *  not for OPBridge's sake but because each tick also SENDS a
   *  `get_device_on` per button into Live, and that part is not free.
   *
   *  Scope: `/devices/status` is in `opbuttons-full` from **v16**. A bearer
   *  minted under v15 or earlier 401s and needs a re-pair to re-mint, which
   *  is indistinguishable from a broken feature unless the caller says so -
   *  `ableton/deviceState.ts` renders that as its own `unauthorized`. */
  /** GET /setlist/state - the current song's values and what was actually
   *  sent to Live.
   *
   *  Scope: the LITERAL `/setlist/state`, added to `opbuttons-full` in v17.
   *  OPBridge measured 31 of 31 bundle-backed tokens gaining it with **no
   *  re-pair**. The grant is read-only and enforced as a literal rather than
   *  a glob, so it deliberately does NOT reach `/setlist/song-change` - a
   *  glob would have handed us the ability to inject songs into the rig.
   *
   *  Polled ONCE on connect for initial state; live updates arrive as the
   *  `setlist_update` push. The frame is deliberately not emitted on
   *  connect, so without this poll a freshly-opened page would sit on
   *  `current: null` until the next song change. */
  setlistState(): Promise<SetlistStateResponse> {
    return this.request<SetlistStateResponse>('GET', '/setlist/state');
  }

  devicesStatus(): Promise<DevicesStatusResponse> {
    return this.request<DevicesStatusResponse>('GET', '/devices/status');
  }

  // ─── Line 6 Helix preset library ────────────────────────────────────

  /** GET /helix/presets — the CACHED preset names. **Never touches USB**, so
   *  this is cheap and safe to call on connect. Default timeout is fine.
   *
   *  A never-probed bridge answers 200 with `status: "never_probed"` and an
   *  empty list — NOT a 404. Callers must read `status`/`device_present`
   *  rather than inferring from the empty array, or "you have not read the
   *  pedal yet" renders as "your pedal has no presets". */
  helixPresets(): Promise<HelixPresetsResponse> {
    return this.request<HelixPresetsResponse>('GET', '/helix/presets');
  }

  /** POST /midi/rescan — drops cached MIDI connections and re-enumerates.
   *
   *  With `restart_if_unrecoverable`, escalates to a DEC-485 daemon restart
   *  (~3s, takes MIDI/DMX/OSC/clock down with it) when OPBridge's own WinMM
   *  session is faulted. That is the ONLY cure for the port wedge a Helix
   *  probe leaves behind — a rescan alone cannot fix it, by OPBridge's own
   *  analysis: `MidiService::send` already re-enumerates on every open, so
   *  nothing is cached to drop.
   *
   *  OPBridge's on-disk cooldown survives the restart it triggers, so a
   *  retry loop here cannot become a restart loop there. */
  midiRescan(restartIfUnrecoverable = false): Promise<Record<string, unknown>> {
    return this.request<Record<string, unknown>>('POST', '/midi/rescan', {
      restart_if_unrecoverable: restartIfUnrecoverable,
    }, { timeoutMs: 15000 });
  }

  /** GET /helix/status — device presence + whether a read is cached.
   *  Enumeration only, opens no USB session, safe to poll. */
  helixDeviceStatus(): Promise<HelixStatusResponse> {
    return this.request<HelixStatusResponse>('GET', '/helix/status');
  }

  /** GET /midi/ports — every MIDI port name the host currently sees.
   *  Enumeration only, no USB session, safe to call freely. Used to
   *  auto-detect a known device's port by name (e.g. a Helix's driver
   *  always names its port containing "Helix") so the operator never has
   *  to type a raw port string for a pedal the app can recognize. */
  async midiPorts(): Promise<MidiPortsResponse> {
    const raw = await this.request<RawMidiPortsResponse>('GET', '/midi/ports');
    const outputs = namesOf(raw.outputs) ?? [];
    const inputs = namesOf(raw.inputs);
    return inputs !== undefined ? { outputs, inputs } : { outputs };
  }

  /** POST /helix/probe — THE EXPENSIVE ONE. Reads 1024 preset names over
   *  USB (~8s measured) and caches them.
   *
   *  Carries its own OPBridge scope, separate from `/helix/presets`, because
   *  it stalls the pedal's MIDI Program Changes until the operator replugs
   *  the USB or opens+quits HX Edit. Reading names must never silently carry
   *  the power to take the pedal offline.
   *
   *  Call this ONLY from an explicit operator action, never on connect —
   *  "connect" also fires on a cable reseat, which can happen mid-gig.
   *  Timeout is generous: a 3s abort would strand a read that is fine and
   *  leave the pedal stalled with nothing cached to show for it. */
  helixProbe(): Promise<HelixProbeResponse> {
    return this.request<HelixProbeResponse>('POST', '/helix/probe', {}, {
      timeoutMs: 30000,
    });
  }

  // ─── Pair-once-forever (v1.14+, DEC-060) ────────────────────────────
  // All four endpoints below live on the DATA-PLANE listener (LAN IP),
  // not admin loopback — see OPBridge router.rs comment on line 1207.
  // Discovery + request endpoints are unauthenticated (public); polling
  // for status is unauthenticated but keyed on request_id.

  /** GET /pairing/consumers — public discovery of consumer registry +
   *  scope bundles. Consumer-name check before firing a pair request. */
  pairingConsumers(): Promise<PairingConsumersResponse> {
    return this.request<PairingConsumersResponse>(
      'GET', '/pairing/consumers', undefined, { auth: false },
    );
  }

  /** POST /pairing/request — open a pairing request against this
   *  bridge. Consumer PWA calls this to trigger the operator-facing
   *  approval banner on the OPBridge dashboard. Returns a `request_id`
   *  to long-poll for the terminal state. */
  pairingRequest(body: PairingRequestBody): Promise<PairingRequestOpenedResponse> {
    return this.request<PairingRequestOpenedResponse>(
      'POST', '/pairing/request', body, { auth: false },
    );
  }

  /** GET /pairing/request/{request_id}?timeout=<ms> — long-poll for the
   *  approval state. Server holds the connection open up to `timeoutMs`
   *  awaiting operator Approve/Deny/TTL. Returns immediately with
   *  status `pending` if the timeout fires without state change. */
  pairingRequestPoll(
    requestId: string,
    timeoutMs = 8000,
  ): Promise<PairingRequestStatusResponse> {
    // Server-side long-poll + a small client buffer so the fetch never
    // races the server's own timeout window. Client timeout must exceed
    // the requested server timeout OR the fetch aborts before the server
    // resolves the wait.
    return this.request<PairingRequestStatusResponse>(
      'GET',
      `/pairing/request/${encodeURIComponent(requestId)}?timeout=${timeoutMs}`,
      undefined,
      { auth: false, timeoutMs: timeoutMs + 2000 },
    );
  }

  /** GET /peers/paired-bridges — authoritative LAN mesh membership known
   *  to this bridge. Requires bearer (any valid one) — returns the peers
   *  the operator has trusted. Consumer PWA calls this on any paired
   *  bridge to render the connected+available mesh in the Connect page. */
  pairedBridges(): Promise<PairedBridgesResponse> {
    return this.request<PairedBridgesResponse>('GET', '/peers/paired-bridges');
  }

  /** GET /pairing/inheritances?since=&bearer=  -  Phase B resync + revocation
   *  probe (see {@link PairingInheritancesResponse}). Backfills peer-bridge
   *  bearers minted while the WS was down and, because OPBridge emits no
   *  `bearer_revoked` push, doubles as the out-of-band revocation check: a
   *  revoked/invalid bearer throws `OpbridgeError` kind 'unauthorized' (401).
   *  The bearer travels as a query param (the endpoint reads `?bearer=`, not
   *  the Authorization header), so this is an `{auth:false}` request that
   *  supplies the token itself. */
  getInheritances(sinceUs: number): Promise<PairingInheritancesResponse> {
    if (!this.bearer) throw new OpbridgeError('unpaired', 'no bearer token configured');
    const since = encodeURIComponent(String(Math.max(0, Math.floor(sinceUs))));
    const q = `?since=${since}&bearer=${encodeURIComponent(this.bearer)}`;
    return this.request<PairingInheritancesResponse>(
      'GET', `/pairing/inheritances${q}`, undefined, { auth: false },
    );
  }

  private async request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    body?: unknown,
    opts: { auth?: boolean; timeoutMs?: number; fetchOnly?: boolean; headers?: Record<string, string> } = {},
  ): Promise<T> {
    const auth = opts.auth ?? true;
    const timeoutMs = opts.timeoutMs ?? this.defaultTimeoutMs;

    // ── WebTransport path (2026-08-31, wired 2026-08-31) ───────────────
    // Same method/path/bearer/body, same OpbridgeError kinds, so every caller
    // above is unchanged. Only reached when a WtConnection was supplied AND
    // it is not in a post-failure cooldown.
    //
    // WHY THIS IS THE PREFERRED PATH RATHER THAN A LAST RESORT: a transport
    // used only when everything else has already failed is a transport whose
    // first real exercise is the night it is needed. Running it continuously
    // means a break shows up at the desk, not at the venue. The fetch code
    // below stays as the fallback and is never removed.
    // A request with headers of its own (the edit lease holder) goes by fetch: OPBridge's WebTransport door does not
    // pass headers on. But offline the suite hostname does not resolve and fetch cannot reach the bridge at all, so
    // with the editor open no timeline could be pushed or started (2026-11-04 timeline audit, OPButtons 4). Then a PUT
    // goes over WebTransport anyway, its headers in the frame for a door that reads them. Only a PUT: a fetch that
    // failed may still have landed, and a PUT replaces, so sending it twice is harmless. OPBridge takes a timeline PUT
    // with no lease header; this device's own lease still locks a displaced editor (it learns on its next renew).
    if (!opts.fetchOnly && opts.headers && method === 'PUT' && this.wt?.usable()) {
      if (Date.now() >= this.fetchDownUntilMs) {
        try {
          return await this.fetchRequest<T>(method, path, body, auth, timeoutMs, opts.headers);
        } catch (err) {
          if (!(err instanceof OpbridgeError) || (err.kind !== 'network' && err.kind !== 'timeout')) throw err;
          this.fetchDownUntilMs = Date.now() + FETCH_DOWN_MS;
        }
      }
      return this.wtRequest<T>(method, path, body, auth, timeoutMs, opts.headers);
    }
    if (!opts.fetchOnly && !opts.headers && this.wt?.usable()) {
      return this.wtRequest<T>(method, path, body, auth, timeoutMs);
    }

    return this.fetchRequest<T>(method, path, body, auth, timeoutMs, opts.headers);
  }

  /** When fetch last failed to reach the bridge for a request that then went over WebTransport: until then such a
   *  request goes straight to WebTransport rather than paying fetch's failure again (`FETCH_DOWN_MS`). */
  private fetchDownUntilMs = 0;

  /** One request over WebTransport (`this.wt`, which the caller has checked is usable). */
  private async wtRequest<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    body: unknown,
    auth: boolean,
    timeoutMs: number,
    headers?: Record<string, string>,
  ): Promise<T> {
    if (auth && !this.bearer) throw new OpbridgeError('unpaired', 'no bearer token configured');
    let res;
    try {
      res = await this.wt!.request(method, path, {
        bearer: auth ? this.bearer : null,
        body,
        timeoutMs,
        ...(headers ? { headers } : {}),
      });
    } catch (err) {
      // A dial failure means NOTHING reached the daemon, so falling through
      // to fetch cannot double-fire a side effect. Anything else happened
      // with the request already on the wire and is surfaced to the caller
      // exactly as a mid-flight fetch failure would be - re-sending a
      // POST /midi/send that actually landed plays the note twice.
      if (!(err instanceof WtTransportError) || err.phase !== 'dial') {
        throw new OpbridgeError('network', err instanceof Error ? err.message : String(err));
      }
      return this.fetchRequest<T>(method, path, body, auth, timeoutMs, headers);
    }
    if (res.status < 200 || res.status >= 300) {
      throw new OpbridgeError(
        res.status === 401 ? 'unauthorized' : 'http',
        `${method} ${path} → ${res.status}${res.body ? `: ${res.body}` : ''}`,
        res.status,
      );
    }
    if (res.status === 204 || method === 'DELETE' || !res.body) return undefined as T;
    return JSON.parse(res.body) as T;
  }

  /** The original `fetch` transport, unchanged in behaviour. Extracted so the
   *  WebTransport branch above can fall back into it on a dial failure
   *  without duplicating the error mapping. */
  private async fetchRequest<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    body: unknown,
    auth: boolean,
    timeoutMs: number,
    extra?: Record<string, string>,
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const headers: Record<string, string> = { ...(extra ?? {}) };
    if (auth) {
      if (!this.bearer) throw new OpbridgeError('unpaired', 'no bearer token configured');
      headers['Authorization'] = `Bearer ${this.bearer}`;
    }
    if (body !== undefined) headers['Content-Type'] = 'application/json';

    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const init: RequestInit = {
        method,
        headers,
        signal: ctrl.signal,
        // Don't send cookies / credentials cross-origin to OPBridge.
        credentials: 'omit',
      };
      if (body !== undefined) init.body = JSON.stringify(body);
      const res = await fetch(url, init);
      if (!res.ok) {
        // Try to parse error body but don't fail if it's not JSON.
        let detail = '';
        try { detail = await res.text(); } catch { /* */ }
        throw new OpbridgeError(
          res.status === 401 ? 'unauthorized' : 'http',
          `${method} ${path} → ${res.status} ${res.statusText}${detail ? `: ${detail}` : ''}`,
          res.status,
        );
      }
      if (res.status === 204 || method === 'DELETE') {
        return undefined as T;
      }
      return await res.json() as T;
    } catch (err) {
      if (err instanceof OpbridgeError) throw err;
      const isAbort = err instanceof DOMException && err.name === 'AbortError';
      throw new OpbridgeError(
        isAbort ? 'timeout' : 'network',
        isAbort ? `timeout after ${timeoutMs}ms` : (err instanceof Error ? err.message : 'fetch failed'),
      );
    } finally {
      clearTimeout(timer);
    }
  }
}

export type OpbridgeErrorKind =
  | 'network'
  | 'timeout'
  | 'http'
  | 'unauthorized'
  | 'unpaired';

export class OpbridgeError extends Error {
  constructor(
    public readonly kind: OpbridgeErrorKind,
    message: string,
    public readonly httpStatus?: number,
  ) {
    super(message);
    this.name = 'OpbridgeError';
  }
}
